import * as O from "fp-ts/Option"
import * as E from "fp-ts/Either"
import * as J from "fp-ts/Json"
import { pipe } from "fp-ts/function"
import { api } from "./api"
import type { AttendanceSheetScan } from "./proof-files"
import {
  needsDepartureTime,
  needsLateMinutes,
  type AttendanceUpdate,
  type ScheduledTimes,
} from "@/lib/attendance-entry"
import { fetchAllPages, type AllPages, type FetchedPage } from "@/lib/paginate"
import type {
  DropoutClosePayload,
  DropoutDocument,
  DropoutRefOption,
  DropoutReminder,
  DropoutStatistics,
  DropoutStatusOption,
} from "@/lib/dropouts"
import type {
  Program,
  ProgramStatus,
  Module,
  Instructor,
  Session,
  SessionListRow,
  SessionStatus,
  Registration,
  RegistrationDetail,
  RegistrationPipeline,
  RegistrationPipelineStageCode,
  PaginatedResponse,
  DropoutListItem,
  DropoutDetail,
  DropoutCorrectiveAction,
  DropoutPreventiveAction,
  MigrationStatusResponse,
  FinancingTypeRef,
  Referential2026Status,
  CertificationTypeRef,
  CertificationTypeCodeWire,
  CertificationTypeLabelWire,
} from "@/types/api"

// Transform backend response to standard PaginatedResponse format
// Backend may return either {data, pagination} or {entity_name, total, page, per_page}
function transformListResponse<T>(response: Record<string, unknown>, dataKey: string): PaginatedResponse<T> {
  // If response already has data/pagination format, return as-is
  if (response.data && response.pagination) {
    return response as unknown as PaginatedResponse<T>
  }

  // Transform entity-specific format to standard format
  const items = response[dataKey] as T[]
  const total = response.total as number
  const page = response.page as number
  const perPage = response.per_page as number

  return {
    data: items,
    pagination: {
      page,
      per_page: perPage,
      total,
      total_pages: Math.ceil(total / perPage),
    },
  }
}

// ==================== Programs ====================

interface ListProgramsParams {
  page?: number
  per_page?: number
  search?: string
  status?: string
  category?: string
  sort_by?: string
  sort_order?: "asc" | "desc"
}

export function listPrograms(params: ListProgramsParams = {}): Promise<PaginatedResponse<Program>> {
  // Translate frontend status filter to backend is_published boolean
  const { status, ...rest } = params
  const apiParams: Record<string, unknown> = { ...rest }
  if (status === "published") {
    apiParams.is_published = true
  } else if (status === "draft") {
    apiParams.is_published = false
  }
  // No backend support for "archived" - omit filter

  return api.get<Record<string, unknown>>("/programs", apiParams)
    .then((response) => {
      const raw = transformListResponse<ProgramApiResponse>(response, "programs")
      return {
        data: raw.data.map(transformProgramResponse),
        pagination: raw.pagination,
      }
    })
}

interface ProgramApiResponse {
  id: string
  code: string
  title: string
  short_description: string | null
  full_description: string | null
  program_type: string
  duration_hours: number
  duration_days: number | null
  price_inter: number | null
  price_intra: number | null
  price_individual: number | null
  pricing_mode: "forfait" | "hourly" | "daily" | null
  hourly_rate_ht: number | null
  daily_rate_ht: number | null
  prerequisites: string | null
  has_no_prerequisites?: boolean
  target_audience: string | null
  max_participants: number | null
  is_published: boolean
  published_at: string | null
  tags: string[] | null
  public_catalog_enabled: boolean | null
  public_catalog_slug: string | null
  public_catalog_sort_order: number | null
  image_url: string | null
  brochure_url: string | null
  nsf_code_id: string | null
  nsf_code: string | null
  nsf_label: string | null
  /** Absent de la liste des programmes, servi par la fiche. */
  // comply-ignore: api-no-internal-ids-in-response — identifiant public d'un modèle d'enquête, celui de GET /survey-templates.
  needs_template_id?: string | null
  certification_type: CertificationTypeCodeWire
  access_modalities: string | null
  access_delay_days: number | null
  assessment_methods: string | null
  teaching_methods: string | null
  accessibility_info: string | null
  objectives: Array<{ id: string; objective_type: string; description: string; sort_order: number }>
  prerequisites_list: Array<{ id: string; description: string; is_mandatory: boolean; verification_method: string | null; sort_order: number }>
  skills: Array<{ id: string; skill_code: string; skill_name: string; target_level: string | null; is_primary: boolean }>
  module_count: number
  created_at: string
  updated_at: string
}

function transformProgramResponse(response: ProgramApiResponse): Program {
  // Compute status from is_published field
  // Backend only has is_published boolean, so we map: published = true -> "published", false -> "draft"
  const status: ProgramStatus = response.is_published ? "published" : "draft"
  // List endpoint returns fewer fields than detail endpoint, so guard optional arrays
  const objectivesArray = Array.isArray(response.objectives) ? response.objectives : []
  const objectives = objectivesArray.map(o => o.description).join("\n")
  const prerequisitesArray = Array.isArray(response.prerequisites_list) ? response.prerequisites_list : []

  return {
    id: response.id,
    tenant_id: "",  // Not returned by API
    title: response.title,
    code: response.code || "",
    description: response.full_description || response.short_description || "",
    short_description: response.short_description || "",
    full_description: response.full_description || "",
    objectives,
    objectives_list: objectivesArray,
    prerequisites: response.prerequisites || "",
    has_no_prerequisites: response.has_no_prerequisites ?? false,
    prerequisites_list: prerequisitesArray,
    target_audience: response.target_audience || "",
    access_modalities: response.access_modalities ?? null,
    access_delay_days: response.access_delay_days ?? null,
    assessment_methods: response.assessment_methods ?? null,
    teaching_methods: response.teaching_methods ?? null,
    accessibility_info: response.accessibility_info ?? null,
    duration_hours: response.duration_hours,
    duration_days: response.duration_days ?? null,
    price: response.price_inter || 0,
    pricing_mode: response.pricing_mode ?? "forfait",
    price_inter: response.price_inter ?? null,
    price_intra: response.price_intra ?? null,
    price_individual: response.price_individual ?? null,
    hourly_rate_ht: response.hourly_rate_ht ?? null,
    daily_rate_ht: response.daily_rate_ht ?? null,
    currency: "EUR",  // Default
    max_participants: response.max_participants || 0,
    status,
    nsf_code_id: response.nsf_code_id || "",
    nsf_code: response.nsf_code || "",
    nsf_label: response.nsf_label || "",
    needs_template_id: O.fromNullable(response.needs_template_id),
    certification_type: O.fromNullable(response.certification_type),
    tags: Array.isArray(response.tags) ? response.tags : [],
    is_published: response.is_published,
    public_catalog_enabled: response.public_catalog_enabled ?? false,
    public_catalog_slug: response.public_catalog_slug || "",
    public_catalog_sort_order: response.public_catalog_sort_order ?? 0,
    image_url: response.image_url || "",
    brochure_url: response.brochure_url || "",
    modules: [],  // Fetched separately
    sessions_count: 0,  // Not in this response
    created_at: response.created_at,
    updated_at: response.updated_at || response.created_at,
  }
}

export function getProgram(id: string): Promise<Program> {
  return api.get<ProgramApiResponse>(`/programs/${id}`).then(transformProgramResponse)
}

export interface ProgramObjectiveInput {
  description: string
}

export interface ProgramPrerequisiteInput {
  description: string
  is_mandatory?: boolean
  verification_method?: string
}

export interface CreateProgramInput {
  title: string
  code: string
  short_description?: string
  full_description?: string
  program_type?: string
  duration_hours: number
  price_inter?: number
  /** Mode tarification : forfait | hourly | daily (cf. ref_pricing_mode). */
  pricing_mode?: "forfait" | "hourly" | "daily"
  hourly_rate_ht?: number
  daily_rate_ht?: number
  target_audience?: string
  access_modalities?: string | null
  access_delay_days?: number | null
  nsf_code_id?: string | null
  objectives?: ProgramObjectiveInput[]
  prerequisites_items?: ProgramPrerequisiteInput[]
  /**
   * Code de GET /ref/certification-types, `none` compris.
   * None : type pas encore choisi.
   */
  certification_type: O.Option<string>
  /** Job d'extraction IA source (quand le programme est créé depuis un import
   *  PDF). Déclenche l'INSERT `program_extraction_audit` côté backend pour la
   *  traçabilité Qualiopi (art. 30 RGPD). Absent pour une création manuelle. */
  source_extraction_job_id?: string
}

export interface UpdateProgramInput {
  title?: string
  code?: string
  description?: string
  short_description?: string
  duration_hours?: number
  price?: number
  /** Mode tarification : forfait | hourly | daily (cf. ref_pricing_mode). */
  pricing_mode?: "forfait" | "hourly" | "daily"
  /** Tarifs forfaitaires (mode = forfait). */
  price_inter?: number | null
  price_intra?: number | null
  price_individual?: number | null
  hourly_rate_ht?: number | null
  daily_rate_ht?: number | null
  target_audience?: string
  access_modalities?: string | null
  access_delay_days?: number | null
  assessment_methods?: string | null
  teaching_methods?: string | null
  accessibility_info?: string | null
  nsf_code_id?: string
  /** Questionnaire de recueil choisi ; none rend le défaut. */
  needs_template_id?: O.Option<string>
  /** Déclaration explicite « aucun prérequis » (Qualiopi, indicateur 1 : information du public). */
  has_no_prerequisites?: boolean
  objectives_list?: ProgramObjectiveInput[]
  prerequisites_items?: ProgramPrerequisiteInput[]
  /** Catégories libres (servent de filtres sur le catalogue public). */
  tags?: string[]
  /** Expose le programme au catalogue public. Refusé (422 PROGRAM_NOT_PUBLISHED) si le programme n'est pas publié. */
  public_catalog_enabled?: boolean
  /** Slug du programme dans l'URL publique. Conflit → 409/422. */
  public_catalog_slug?: string
  /** Ordre d'affichage dans le catalogue public. */
  public_catalog_sort_order?: number
  /** Code du type de reconnaissance choisi, `none` compris. */
  certification_type?: string
}

/** Champs facultatifs de la création, recopiés s'ils sont fournis. */
const OPTIONAL_CREATE_FIELDS = [
  "short_description",
  "full_description",
  "program_type",
  "price_inter",
  "pricing_mode",
  "hourly_rate_ht",
  "daily_rate_ht",
  "target_audience",
  "access_modalities",
  "access_delay_days",
  "objectives",
  "prerequisites_items",
  "source_extraction_job_id",
] as const satisfies readonly (keyof CreateProgramInput)[]

/**
 * Corps de POST /programs : seuls les champs fournis partent.
 * @example createProgramPayload({ title: "SST", code: "SST-01", duration_hours: 14, certification_type: O.some("none") })
 * // { title: "SST", code: "SST-01", duration_hours: 14, certification_type: "none" }
 */
export function createProgramPayload(program: CreateProgramInput): Record<string, unknown> {
  const programFields: Record<string, unknown> = {
    title: program.title,
    code: program.code,
    duration_hours: program.duration_hours,
  }
  for (const field of OPTIONAL_CREATE_FIELDS) {
    if (program[field] !== undefined) programFields[field] = program[field]
  }
  if (program.nsf_code_id !== undefined) programFields.nsf_code_id = program.nsf_code_id || null
  if (O.isSome(program.certification_type)) programFields.certification_type = program.certification_type.value
  return programFields
}

export function createProgram(program: CreateProgramInput): Promise<Program> {
  return api.post<ProgramApiResponse>("/programs", createProgramPayload(program)).then(transformProgramResponse)
}

export interface ProgramSkillInput {
  skill_id: string
  target_level_id?: string
  is_primary?: boolean
}

export interface ModuleSkillInput {
  skill_id: string
  expected_level_id?: string
  is_primary?: boolean
}

interface UpdateProgramApiRequest {
  title?: string
  code?: string
  short_description?: string
  full_description?: string
  duration_hours?: number
  price_inter?: number | null
  price_intra?: number | null
  price_individual?: number | null
  pricing_mode?: "forfait" | "hourly" | "daily"
  hourly_rate_ht?: number | null
  daily_rate_ht?: number | null
  target_audience?: string
  access_modalities?: string | null
  access_delay_days?: number | null
  assessment_methods?: string | null
  teaching_methods?: string | null
  accessibility_info?: string | null
  nsf_code_id?: string | null
  needs_template_id?: string | null
  has_no_prerequisites?: boolean
  objectives?: ProgramObjectiveInput[]
  prerequisites_items?: ProgramPrerequisiteInput[]
  tags?: string[]
  public_catalog_enabled?: boolean
  public_catalog_slug?: string
  public_catalog_sort_order?: number
  certification_type?: string
}

export function updateProgram(id: string, data: UpdateProgramInput): Promise<Program> {
  const request: UpdateProgramApiRequest = {}

  if (data.title !== undefined) request.title = data.title
  if (data.code !== undefined) request.code = data.code
  if (data.description !== undefined) request.full_description = data.description
  if (data.short_description !== undefined) request.short_description = data.short_description
  if (data.duration_hours !== undefined) request.duration_hours = data.duration_hours
  if (data.price !== undefined) request.price_inter = data.price
  if (data.price_inter !== undefined) request.price_inter = data.price_inter
  if (data.price_intra !== undefined) request.price_intra = data.price_intra
  if (data.price_individual !== undefined) request.price_individual = data.price_individual
  if (data.pricing_mode !== undefined) request.pricing_mode = data.pricing_mode
  if (data.hourly_rate_ht !== undefined) request.hourly_rate_ht = data.hourly_rate_ht
  if (data.daily_rate_ht !== undefined) request.daily_rate_ht = data.daily_rate_ht
  if (data.target_audience !== undefined) request.target_audience = data.target_audience
  if (data.access_modalities !== undefined) request.access_modalities = data.access_modalities
  if (data.access_delay_days !== undefined) request.access_delay_days = data.access_delay_days
  if (data.assessment_methods !== undefined) request.assessment_methods = data.assessment_methods
  if (data.teaching_methods !== undefined) request.teaching_methods = data.teaching_methods
  if (data.accessibility_info !== undefined) request.accessibility_info = data.accessibility_info
  if (data.nsf_code_id !== undefined) request.nsf_code_id = data.nsf_code_id || null
  if (data.needs_template_id !== undefined) request.needs_template_id = O.toNullable(data.needs_template_id)
  if (data.objectives_list !== undefined) request.objectives = data.objectives_list
  if (data.prerequisites_items !== undefined) request.prerequisites_items = data.prerequisites_items
  if (data.has_no_prerequisites !== undefined) request.has_no_prerequisites = data.has_no_prerequisites
  if (data.tags !== undefined) request.tags = data.tags
  if (data.public_catalog_enabled !== undefined) request.public_catalog_enabled = data.public_catalog_enabled
  if (data.public_catalog_slug !== undefined) request.public_catalog_slug = data.public_catalog_slug
  if (data.public_catalog_sort_order !== undefined) request.public_catalog_sort_order = data.public_catalog_sort_order
  if (data.certification_type !== undefined) request.certification_type = data.certification_type

  return api.put<ProgramApiResponse>(`/programs/${id}`, request).then(transformProgramResponse)
}

export function deleteProgram(id: string): Promise<void> {
  return api.delete(`/programs/${id}`)
}

/**
 * Publie un programme. Côté backend, déclenche la transition d'une version
 * (création V1.0.0 si aucune n'existe, sinon transition draft/review → active,
 * sinon création V(N+1).0.0). Les 15 gardes Qualiopi sont évaluées via
 * `compute_publish_blockers` ; si l'une bloque, le backend renvoie 422 avec
 * `{ blockers: [...] }` consommé par l'encart « À compléter avant de publier ».
 */
export function publishProgram(id: string): Promise<Program> {
  return api.post<ProgramApiResponse>(`/programs/${id}/publish`, {}).then(transformProgramResponse)
}

/**
 * Dépublie un programme : transitionne la version active courante vers
 * `deprecated` + désactive `public_catalog_enabled` automatiquement côté
 * backend. Pas de garde Qualiopi (la dépublication est toujours autorisée) ;
 * les sessions en cours restent inchangées (Qualiopi Ind. 9).
 */
export function unpublishProgram(id: string): Promise<Program> {
  return api.post<ProgramApiResponse>(`/programs/${id}/unpublish`, {}).then(transformProgramResponse)
}

/**
 * État de migration Qualiopi côté tenant : liste les programmes backfillés
 * par la migration 230 qui présentent encore au moins un bloqueur. Alimente
 * la bannière `MigrationBanner` au-dessus de la liste des programmes.
 * Réponse {affected_count: 0, ...} ⇒ rien à afficher.
 */
export function getMigrationStatus(): Promise<MigrationStatusResponse> {
  return api.get<MigrationStatusResponse>("/programs/migration-status")
}

// ==================== Program Import (Import IA depuis PDF) ====================
//
// Mirrors backend `src/api/handlers/program_imports.rs`. The import is fully
// async (Issue #34, §12bis decision 10): POST returns 202 with a job id, the
// frontend polls GET /programs/imports/:id every ~2 s until the job reaches a
// terminal state (`succeeded` / `failed`). No SSE in phase 1.

export type ProgramImportStatus =
  | "pending"
  | "running"
  | "succeeded"
  | "failed"
  | "review_required"

/** Champs extraits par le LLM depuis le PDF source. Tout champ peut être `null`
 *  quand l'IA ne l'a pas trouvé explicitement dans le document (jamais inventé).
 *  Forme alignée sur le JSON Schema `ProgramExtracted` du backend. */
export interface ExtractedProgram {
  titre: string | null
  objectifs: string[] | null
  duree_heures: number | null
  prerequis: string[] | null
  modalites: string | null
  public_vise: string | null
  modalites_evaluation: string | null
  tarif_ht_eur: number | null
  is_training_program: boolean
}

export interface ProgramImport {
  id: string
  status: ProgramImportStatus
  /** Présent uniquement quand `status === "succeeded"` (ou `review_required`). */
  extracted_data: ExtractedProgram | null
  /** Message d'erreur lisible quand `status === "failed"`. */
  error_message: string | null
  /** Identifiant du fichier source stocké (file_storage), pour ouvrir le PDF. */
  source_file_id: string | null
}

export interface CreateProgramImportResponse {
  job_id: string
  status: ProgramImportStatus
}

/** Téléverse un PDF de programme et lance l'extraction IA asynchrone.
 *  Retourne immédiatement le job (202) ; suivre l'avancement via
 *  `getProgramImport(job_id)`. */
export function createProgramImport(file: File): Promise<CreateProgramImportResponse> {
  const form = new FormData()
  form.append("file", file)
  return api.postForm<CreateProgramImportResponse>("/programs/imports", form)
}

/** Récupère l'état courant d'un job d'import IA (polling). */
export function getProgramImport(id: string): Promise<ProgramImport> {
  return api.get<ProgramImport>(`/programs/imports/${id}`)
}

// ==================== Illustration & brochure de programme ====================

/** Types MIME image acceptés par l'upload d'illustration (miroir du backend). */
export const PROGRAM_ILLUSTRATION_ACCEPT = ["image/png", "image/jpeg", "image/webp"] as const
/** Taille max d'une illustration (5 Mo), miroir du body-limit backend. */
export const PROGRAM_ILLUSTRATION_MAX_BYTES = 5 * 1024 * 1024
/** Type MIME accepté par l'upload de brochure. */
export const PROGRAM_BROCHURE_ACCEPT = ["application/pdf"] as const
/** Taille max d'une brochure PDF (20 Mo), miroir du body-limit backend. */
export const PROGRAM_BROCHURE_MAX_BYTES = 20 * 1024 * 1024

/** Résultat d'une validation client de fichier avant upload. Discriminé par `ok`
 *  pour forcer l'appelant à traiter le message d'erreur (pas de `null`). */
export type FileValidation =
  | { ok: true }
  | { ok: false; message: string }

/** Formate une taille en octets en libellé « Mo » lisible (borné à 1 décimale). */
function formatMegabytes(bytes: number): string {
  return `${Math.round((bytes / (1024 * 1024)) * 10) / 10} Mo`
}

/**
 * Valide côté client (avant tout aller-retour réseau) qu'un fichier respecte
 * les bornes type + taille attendues. Fonction PURE : mêmes entrées → même
 * sortie, aucun effet de bord. Invariants garantis :
 *  - un `type` hors `acceptedTypes` ⇒ `{ ok: false }` (message « format »),
 *  - une `size` strictement au-dessus de `maxBytes` ⇒ `{ ok: false }` (message « taille »),
 *  - type valide ET `size <= maxBytes` ⇒ `{ ok: true }`.
 * Le contrôle de type prime sur celui de taille (un fichier au mauvais format
 * ET trop lourd renvoie le message « format »).
 */
export function validateUploadFile(
  file: { type: string; size: number },
  acceptedTypes: readonly string[],
  maxBytes: number,
): FileValidation {
  if (!acceptedTypes.includes(file.type)) {
    return { ok: false, message: "Format de fichier non supporté." }
  }
  if (file.size > maxBytes) {
    return { ok: false, message: `Fichier trop volumineux (max ${formatMegabytes(maxBytes)}).` }
  }
  return { ok: true }
}

/** Convertit l'URL publique d'un asset programme (`image_url`, servie par
 *  `/api/v1/public/programs/...` et gatée sur la publication) en URL **in-app
 *  authentifiée** (`/api/v1/programs/...`, route RBAC Training:Read), pour l'aperçu
 *  AVANT publication — la route publique répondrait 404 sur un brouillon. Une URL
 *  externe (ancienne URL Drive) ne matche pas le préfixe et est laissée telle quelle. */
export function programAssetInAppUrl(publicUrl: string): string {
  return publicUrl.replace("/api/v1/public/programs/", "/api/v1/programs/")
}

/** Téléverse (ou remplace) l'illustration catalogue d'un programme.
 *  Réponse `{ image_url }` (URL de route app auto-hébergée avec cache-busting). */
export function uploadProgramIllustration(id: string, file: File): Promise<{ image_url: string }> {
  const form = new FormData()
  form.append("file", file)
  return api.postForm<{ image_url: string }>(`/programs/${id}/illustration`, form)
}

/** Retire l'illustration d'un programme (`image_url` repassé à vide). */
export function deleteProgramIllustration(id: string): Promise<void> {
  return api.delete<void>(`/programs/${id}/illustration`)
}

/** Téléverse (ou remplace) la brochure PDF d'un programme.
 *  Réponse `{ brochure_url }`. */
export function uploadProgramBrochure(id: string, file: File): Promise<{ brochure_url: string }> {
  const form = new FormData()
  form.append("file", file)
  return api.postForm<{ brochure_url: string }>(`/programs/${id}/brochure`, form)
}

/** Retire la brochure PDF d'un programme (`brochure_url` repassé à vide). */
export function deleteProgramBrochure(id: string): Promise<void> {
  return api.delete<void>(`/programs/${id}/brochure`)
}

// ==================== Modules ====================

interface ListModulesParams {
  page?: number
  per_page?: number
  program_id?: string
}

export function listModules(programId: string, params: ListModulesParams = {}): Promise<PaginatedResponse<Module>> {
  return api.get<Record<string, unknown>>(`/programs/${programId}/modules`, params as Record<string, unknown>)
    .then((response) => transformListResponse<Module>(response, "modules"))
}

export function getModule(programId: string, moduleId: string): Promise<Module> {
  return api.get<Module>(`/programs/${programId}/modules/${moduleId}`)
}

export function createModule(programId: string, data: Partial<Module>): Promise<Module> {
  return api.post<Module>(`/programs/${programId}/modules`, data)
}

export function updateModule(programId: string, moduleId: string, data: Partial<Module>): Promise<Module> {
  return api.put<Module>(`/programs/${programId}/modules/${moduleId}`, data)
}

export function deleteModule(programId: string, moduleId: string): Promise<void> {
  return api.delete(`/programs/${programId}/modules/${moduleId}`)
}

// ==================== Program Success Criteria (Qualiopi Ind. 11) ====================

export interface ProgramSuccessCriterion {
  id: string
  description: string
  minimum_score: number | null
  measurement_method: string
  is_mandatory: boolean
  sort_order: number
}

export interface CreateSuccessCriterionInput {
  description: string
  minimum_score?: number | null
  measurement_method: string
  is_mandatory?: boolean
  sort_order?: number
}

export interface UpdateSuccessCriterionInput {
  description?: string
  minimum_score?: number | null
  measurement_method?: string
  is_mandatory?: boolean
  sort_order?: number
}

/**
 * Types de financement de l'organisme (système et propres).
 * @example listFinancingTypes().then((types) => types.map((type) => type.code))
 * // ["opco", "cpf", "employer", "self"]
 */
export function listFinancingTypes(): Promise<FinancingTypeRef[]> {
  return api.get<{ data: FinancingTypeRef[] }>("/ref/financing-types").then((response) => response.data)
}

/**
 * Types de reconnaissance, `none` compris.
 * Ceux du système et ceux de l'organisme.
 * @example listCertificationTypes().then((types) => types.map((type) => type.code))
 * // ["rncp", "rs", "cqp", "internal", "none"]
 */
export function listCertificationTypes(): Promise<CertificationTypeRef[]> {
  return api.get<{ data: CertificationTypeRef[] }>("/ref/certification-types").then((response) => response.data)
}

/**
 * Modalités de financement déclarées pour un programme (indicateur 1).
 * @example listProgramFinancingTypes("p1").then((types) => types.length) // 2
 */
// comply-ignore: api-branded-id-types — les services du dépôt prennent des identifiants chaîne.
export function listProgramFinancingTypes(programId: string): Promise<FinancingTypeRef[]> {
  return api
    .get<{ data: FinancingTypeRef[] }>(`/programs/${programId}/financing-types`)
    .then((response) => response.data)
}

/**
 * Remplace les modalités de financement d'un programme (au plus 20).
 * @example replaceProgramFinancingTypes("p1", ["f-opco"]) // Promise<[OPCO]>
 */
// comply-ignore: api-branded-id-types — les services du dépôt prennent des identifiants chaîne.
export function replaceProgramFinancingTypes(programId: string, financingTypeIds: string[]): Promise<FinancingTypeRef[]> {
  return api
    .put<{ data: FinancingTypeRef[] }>(`/programs/${programId}/financing-types`, { financing_type_ids: financingTypeIds })
    .then((response) => response.data)
}

/**
 * Programmes publiés à mettre en règle au 1er novembre 2026.
 * @example getReferential2026Status().then((status) => status.affected_count) // 0
 */
export function getReferential2026Status(): Promise<Referential2026Status> {
  return api.get<Referential2026Status>("/programs/referential-2026-status")
}

export function listProgramSuccessCriteria(programId: string): Promise<ProgramSuccessCriterion[]> {
  return api.get<ProgramSuccessCriterion[]>(`/programs/${programId}/success-criteria`)
}

export function createProgramSuccessCriterion(programId: string, data: CreateSuccessCriterionInput): Promise<ProgramSuccessCriterion> {
  return api.post<ProgramSuccessCriterion>(`/programs/${programId}/success-criteria`, data)
}

export function updateProgramSuccessCriterion(programId: string, criterionId: string, data: UpdateSuccessCriterionInput): Promise<ProgramSuccessCriterion> {
  return api.put<ProgramSuccessCriterion>(`/programs/${programId}/success-criteria/${criterionId}`, data)
}

export function deleteProgramSuccessCriterion(programId: string, criterionId: string): Promise<void> {
  return api.delete(`/programs/${programId}/success-criteria/${criterionId}`)
}

// ==================== Session Module Completion (Qualiopi Ind. 11) ====================

export interface CompleteSessionModuleResponse {
  session_id: string
  module_id: string
  session_module_id: string
  status: string
  jobs_enqueued: number
}

/** Marks a session module as completed and enqueues SEND_FORMATIVE_QUIZ jobs
 *  for every quiz flagged `auto_trigger=true` on that module, dispatched to
 *  every active learner registered on the session. Qualiopi Ind. 11.
 *  Idempotent — second call returns `jobs_enqueued: 0` unless `force=true`. */
export function completeSessionModule(
  sessionId: string,
  moduleId: string,
  options: { force?: boolean } = {},
): Promise<CompleteSessionModuleResponse> {
  return api.post<CompleteSessionModuleResponse>(
    `/event-sessions/${sessionId}/modules/${moduleId}/complete`,
    options,
  )
}

export interface ComputePassingStatusResponse {
  registration_id: string
  passing_status: "passed" | "failed" | "partial"
  passing_status_at: string
}

/** Compute and persist passing_status for a registration based on
 *  edu_program_success_criterion mandatory checks + final_score.
 *  Called automatically by state machine on Attending → Completed; this is
 *  the manual trigger. Qualiopi Ind. 11. */
export function computePassingStatus(registrationId: string): Promise<ComputePassingStatusResponse> {
  return api.post<ComputePassingStatusResponse>(`/registrations/${registrationId}/compute-passing-status`, {})
}

// ==================== Sessions ====================

interface ListSessionsParams {
  page?: number
  per_page?: number
  search?: string
  program_id?: string
  status?: string
  start_date?: string
  end_date?: string
  /** `"pending"` : sessions ayant au moins un formateur affecté mais pas encore confirmé (Ind. 18). */
  instructor_confirmation?: string
  /** `"pending"` : sessions terminées à évaluer. Liste de la carte « Sessions à évaluer ». */
  evaluation?: string
  /** `true` : sessions qui ont lieu (ni brouillon, annulée, reportée). */
  takes_place?: boolean
}

export function listSessions(params: ListSessionsParams = {}): Promise<PaginatedResponse<Session>> {
  return api.get<Record<string, unknown>>("/sessions", params as Record<string, unknown>)
    .then((response) => transformListResponse<Session>(response, "sessions"))
}

/** Plafond de l'onglet « Sessions animées » : les plus récentes d'abord. */
const INSTRUCTOR_SESSIONS_PAGE_SIZE = 100

/**
 * Sessions où ce profil formateur est affecté.
 * Le filtre est appliqué par le serveur.
 *
 * @example
 * listInstructorSessions({ id: "p1" }) // { data: [{ code: "SES-01", status_label: "Planifiée", … }], pagination }
 */
export function listInstructorSessions(
  profile: Pick<Instructor, "id">,
): Promise<PaginatedResponse<SessionListRow>> {
  return api
    .get<Record<string, unknown>>("/sessions", {
      instructor_profile_id: profile.id,
      per_page: INSTRUCTOR_SESSIONS_PAGE_SIZE,
    })
    .then((response) => transformListResponse<SessionListRow>(response, "sessions"))
}

export function getSession(id: string): Promise<Session> {
  return api.get<Session>(`/sessions/${id}`)
}

/** Livrable du programme, dans la liste d'une session. */
export type SessionDeliverable = {
  id: string
  /** Id du livrable du programme : clé de la coche. */
  deliverable_id: string
  deliverable_name: string
  deliverable_type: string
  responsible_role: string
  is_mandatory: boolean
  description: string | null
  is_completed: boolean
  completed_by: string | null
  /** Auteur de la coche, `null` s'il n'est plus membre. */
  completed_by_name: string | null
  completed_at: string | null
  notes: string | null
  /**
   * Jour d'échéance (AAAA-MM-JJ), `null` sans délai.
   * À faire : début de la session moins le délai.
   * Fait : l'échéance du jour de la coche.
   */
  due_date: string | null
  is_overdue: boolean
  /**
   * Livrable d'un ancien programme, servi parce qu'il est coché.
   * Le décocher le retire de la checklist.
   */
  from_former_program: boolean
}

export type SessionDeliverableProgress = {
  total: number
  completed: number
  pending: number
  overdue: number
  completion_percentage: number
}

export type SessionDeliverablesResponse = {
  session_id: string
  /**
   * `open` : un livrable ajouté au programme la rejoint.
   * `closed` : session terminée ou annulée.
   */
  checklist_intake: "open" | "closed"
  deliverables: SessionDeliverable[]
  progress: SessionDeliverableProgress
}

export const sessionDeliverableKeys = {
  list: (sessionId: string) => ["session-deliverables", sessionId],
}

/**
 * Liste des livrables d'une session et sa progression.
 *
 * @example
 * listSessionDeliverables(sessionId) // { deliverables: [...], progress: { total: 3, completed: 1, ... } }
 */
// comply-ignore: api-branded-id-types — les services du dépôt prennent des identifiants chaîne.
export function listSessionDeliverables(sessionId: string): Promise<SessionDeliverablesResponse> {
  return api.get<SessionDeliverablesResponse>(`/sessions/${sessionId}/deliverables`)
}

/**
 * Coche ou décoche un livrable de la session.
 *
 * @example
 * setSessionDeliverableCompleted(sessionId, deliverableId, true) // { is_completed: true, completed_at: "…" }
 */
// comply-ignore: api-branded-id-types — les services du dépôt prennent des identifiants chaîne.
export function setSessionDeliverableCompleted(sessionId: string, deliverableId: string, isCompleted: boolean): Promise<SessionDeliverable> {
  return api.put<SessionDeliverable>(`/sessions/${sessionId}/deliverables/${deliverableId}`, {
    is_completed: isCompleted,
  })
}

export function createSession(data: Partial<Session>): Promise<Session> {
  return api.post<Session>("/sessions", data)
}

export interface SessionInstructorInput {
  instructor_id: string
  /** Optionnel : défaut « main_trainer » côté API. */
  role_id?: string
  assigned_modules?: string[]
  hourly_rate?: number
  total_hours?: number
  total_amount?: number
  internal_notes?: string
  /** `true` désaffecte le formateur ; les autres restent. */
  remove?: boolean
}

/** Rôle de formateur du référentiel (`GET /ref/instructor-roles`, main_trainer en premier). */
export type RefInstructorRole = {
  id: string
  code: string
  label: string
  is_lead: boolean
  sort_order: number
}

export function listInstructorRoles(): Promise<RefInstructorRole[]> {
  return api.get<RefInstructorRole[]>("/ref/instructor-roles")
}

// `instructors` de lecture (SessionInstructorRef) est remplacé par le format
// d'écriture (SessionInstructorInput) — l'intersection brute des deux types
// serait insatisfiable.
/**
 * Mise à jour d'une session. `actualEnd` : fin réelle de l'action à poser
 * (`some(some(date))`), à retirer (`some(none)`), ou laissée telle quelle
 * (`none`, la clé n'est pas envoyée).
 */
/** Session modifiée, et feuilles vides retirées par la fin réelle. */
export type UpdatedSession = Session & { removed_sheet_count: number }

export function updateSession(
  id: string,
  data: Omit<Partial<Session>, "instructors"> & { instructors?: SessionInstructorInput[] },
  actualEnd: O.Option<O.Option<string>> = O.none,
  capacity: O.Option<O.Option<number>> = O.none,
): Promise<UpdatedSession> {
  return api.put<UpdatedSession>(`/sessions/${id}`, {
    ...data,
    ...pipe(
      actualEnd,
      O.match(
        () => ({}),
        (end) => ({ actual_end_date: O.toNullable(end) }),
      ),
    ),
    // Capacité vidée : `null` envoyé, la session redevient sans limite.
    ...pipe(
      capacity,
      O.match(
        () => ({}),
        (max) => ({ max_participants: O.toNullable(max) }),
      ),
    ),
  })
}

/**
 * Fin réelle lue dans le détail d'une session (`actual_end_date`, `null` tant
 * qu'elle n'est pas posée) ; une valeur qui n'est pas une date `AAAA-MM-JJ`
 * est ignorée.
 *
 * @example
 * sessionActualEndDate({ actual_end_date: "2026-09-08" }) // O.some("2026-09-08")
 * sessionActualEndDate({ actual_end_date: null }) // O.none
 */
export function sessionActualEndDate(session: Pick<Session, "actual_end_date">): O.Option<string> {
  const raw = session.actual_end_date
  return typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? O.some(raw) : O.none
}

/** Champ lu par `sessionCapacity`, servi `null` sans capacité. */
type SessionCapacitySource = { max_participants: unknown }

/**
 * Capacité d'une session, absente en brouillon.
 * Sans capacité, l'inscription n'a pas de limite.
 *
 * @example
 * sessionCapacity({ max_participants: 12 }) // O.some(12)
 * sessionCapacity({ max_participants: null }) // O.none
 */
export function sessionCapacity(session: SessionCapacitySource): O.Option<number> {
  const raw = session.max_participants
  return typeof raw === "number" && Number.isFinite(raw) ? O.some(raw) : O.none
}

/** Libellé d'une session sans capacité, le même sur chaque écran. */
export const UNDEFINED_CAPACITY_LABEL = "Capacité non définie"

/**
 * Inscrits sur la capacité : « 3/12 », ou « 3 · Capacité non définie ».
 *
 * @example
 * participantsOverCapacity(3, { max_participants: 12 }) // "3/12"
 * participantsOverCapacity(3, { max_participants: null }) // "3 · Capacité non définie"
 */
export function participantsOverCapacity(count: number, session: SessionCapacitySource): string {
  return pipe(
    sessionCapacity(session),
    O.match(
      () => `${count} · ${UNDEFINED_CAPACITY_LABEL}`,
      (max) => `${count}/${max}`,
    ),
  )
}

/**
 * Capacité saisie à la création.
 * Champ vide : sans capacité, la clé n'est pas envoyée.
 *
 * @example
 * capacityFromInput("12") // O.some(12)
 * capacityFromInput("") // O.none
 */
export function capacityFromInput(raw: string): O.Option<number> {
  const trimmed = raw.trim()
  if (trimmed === "") return O.none
  const capacity = Number(trimmed)
  return Number.isFinite(capacity) ? O.some(capacity) : O.none
}

export function deleteSession(id: string): Promise<void> {
  return api.delete(`/sessions/${id}`)
}

export interface TransitionSessionRequest {
  target_status: string
}

export interface TransitionSessionResponse {
  success: boolean
  message: string
  session_id: string
  from_status: string
  to_status: string
  /** Codes des gardes levées par un forçage administrateur (vide sans forçage). */
  forced_guards: string[]
  /** Feuilles d'émargement vides retirées par la fin anticipée (0 sinon). */
  removed_sheet_count: number
}

export function transitionSession(id: string, data: TransitionSessionRequest): Promise<TransitionSessionResponse> {
  return api.post<TransitionSessionResponse>(`/sessions/${id}/transition`, data)
}

/**
 * Transition forcée (`admin_override`) : le serveur ne l'accorde qu'à un
 * administrateur ; elle passe outre les gardes de seuil, de dates, de
 * présence établie, de certificats et d'enquête.
 */
export function forceTransitionSession(id: string, targetStatus: string): Promise<TransitionSessionResponse> {
  return api.post<TransitionSessionResponse>(`/sessions/${id}/transition`, {
    target_status: targetStatus,
    admin_override: true,
  })
}

// ==================== Session Slots ====================

export interface SessionSlot {
  id: string
  slot_date: string
  start_time: string
  end_time: string
  duration_hours: number
  slot_type: string
  location: string | null
  room: string | null
  online_url: string | null
  is_cancelled: boolean
  /** Créneau de pause (déjeuner…) — exclu des heures de formation. */
  is_break: boolean
  /** Formateur désigné (`crm_instructor.id`) ; `null` : les formateurs de la session. */
  instructor_id: string | null
  instructor_name: string | null
  /** Fiche RH du formateur désigné. */
  // comply-ignore: api-no-internal-ids-in-response — cible du lien /hr/instructors/:id, servie telle quelle.
  instructor_profile_id: string | null
  /** Module du programme couvert ; `null` : non précisé. */
  module_id: string | null
  module_title: string | null
}

export interface SessionSlotsResponse {
  slots: SessionSlot[]
  total: number
  page: number
  per_page: number
}

/** Tous les créneaux de la session, page par page (100 par page, plafond de `fetchAllPages`). */
export function listAllSessionSlots(sessionId: string): Promise<AllPages<SessionSlot>> {
  return fetchAllPages((page) =>
    api
      .get<SessionSlotsResponse>(`/sessions/${sessionId}/slots`, { page, per_page: 100 })
      .then((response) => ({ items: response.slots, totalPages: Math.ceil(response.total / response.per_page) })),
  )
}

// ==================== Session instructor invitation ====================

export interface InviteInstructorResponse {
  sent: boolean
  already_pending: boolean
  invitation_sent_at: string | null
}

/** Send (or resend) a confirmation invitation email to a trainer on a session. */
export function inviteSessionInstructor(
  sessionId: string,
  instructorId: string,
): Promise<InviteInstructorResponse> {
  return api.post<InviteInstructorResponse>(
    `/sessions/${sessionId}/instructors/${instructorId}/invite`,
    {},
  )
}

// ==================== Calendar ====================

/**
 * One calendar event = one teaching slot (créneau). For sessions that have no
 * slots defined yet, the API returns a single marker on the session start_date
 * with `has_slots: false` (so the UI can explain why and link to the planning).
 */
export interface CalendarEventItem {
  session_id: string
  slot_id: string | null
  code: string
  program_name: string | null
  status: SessionStatus
  location: string | null
  /** YYYY-MM-DD */
  date: string
  /** HH:MM:SS — null on the fallback marker. */
  start_time: string | null
  end_time: string | null
  has_slots: boolean
  /** Active (non-cancelled) registrations on the session. `0` when empty. */
  attendee_count: number
  /** Distinct registrant company names, alphabetical, capped at five. */
  company_names: string[]
  /** Total distinct companies (may exceed `company_names.length` → « +N »). */
  company_count: number
}

export interface CalendarEventsResponse {
  events: CalendarEventItem[]
}

/** Fetch calendar events between two inclusive ISO dates (YYYY-MM-DD). */
export function listCalendarEvents(from: string, to: string): Promise<CalendarEventsResponse> {
  return api.get<CalendarEventsResponse>("/calendar/events", { from, to } as Record<string, unknown>)
}

export interface CreateSlotRequest {
  date: string
  start_time: string
  end_time: string
  slot_type_id: string
  venue_id?: string
  room?: string
  remote_url?: string
  instructor_id?: string
  /** Module du programme couvert par le créneau. */
  module_id?: string
  title?: string
  description?: string
  is_break?: boolean
}

export interface SlotResponse {
  id: string
  session_id: string
  date: string
  start_time: string
  end_time: string
  duration_hours: number
  slot_type_id: string
  slot_type: string
  venue_id: string | null
  venue_name: string | null
  room: string | null
  remote_url: string | null
  instructor_id: string | null
  instructor_name: string | null
  // comply-ignore: api-no-internal-ids-in-response — cible du lien /hr/instructors/:id, servie telle quelle.
  instructor_profile_id: string | null
  // comply-ignore: api-no-internal-ids-in-response — identifiant public du module, tel que l'API le sert.
  module_id: string | null
  module_title: string | null
  title: string | null
  description: string | null
  is_cancelled: boolean
  cancelled_reason: string | null
  is_break: boolean
  created_at: string
  updated_at: string
}

export function createSessionSlot(sessionId: string, data: CreateSlotRequest): Promise<SlotResponse> {
  return api.post<SlotResponse>(`/sessions/${sessionId}/slots`, data)
}

export interface UpdateSlotRequest {
  date?: string
  start_time?: string
  end_time?: string
  slot_type_id?: string
  venue_id?: string
  room?: string
  remote_url?: string
  instructor_id?: string
  module_id?: string
  title?: string
  description?: string
  is_cancelled?: boolean
  cancelled_reason?: string
  is_break?: boolean
}

/** Type de créneau du référentiel (`GET /ref/slot-types`). */
export interface RefSlotType {
  id: string
  code: string
  label: string
  sort_order: number
}

/** Types de créneau actifs de l'organisme : choisir sur `code`, envoyer `id`. */
export function listSlotTypes(): Promise<RefSlotType[]> {
  return api.get<RefSlotType[]>("/ref/slot-types")
}

/** Statut d'inscription du référentiel (`GET /ref/registration-statuses`). */
export interface RefRegistrationStatus {
  id: string
  code: string
  label: string
  is_confirmed: boolean
  is_cancelled: boolean
  is_terminal: boolean
  sort_order: number
  /** Couleur du badge (`#RRGGBB`), `null` : neutre. */
  color: string | null
}

/** Statuts d'inscription actifs de l'organisme : choisir sur `code`, envoyer `id`. */
export function listRegistrationStatuses(): Promise<RefRegistrationStatus[]> {
  return api.get<RefRegistrationStatus[]>("/ref/registration-statuses")
}

export function updateSlot(slotId: string, data: UpdateSlotRequest): Promise<SlotResponse> {
  return api.put<SlotResponse>(`/slots/${slotId}`, data)
}

/**
 * Met à jour un créneau.
 * Formateur et module ne partent que s'ils sont touchés.
 * Choix absent : clé absente, valeur inchangée.
 * Choix `none` : clé envoyée à `null`.
 *
 * @example
 * updateSlotAssignment("s1", {}, O.none, O.some(O.none))
 */
export function updateSlotAssignment(
  slotId: string,
  data: UpdateSlotRequest,
  instructor: O.Option<O.Option<string>>,
  module: O.Option<O.Option<string>>,
): Promise<SlotResponse> {
  return api.put<SlotResponse>(`/slots/${slotId}`, {
    ...data,
    ...pipe(
      instructor,
      O.match(
        () => ({}),
        (choice) => ({ instructor_id: O.toNullable(choice) }),
      ),
    ),
    ...pipe(
      module,
      O.match(
        () => ({}),
        (choice) => ({ module_id: O.toNullable(choice) }),
      ),
    ),
  })
}

/** Créneau relu seul (`GET /slots/:id`). */
export function getSlot(slotId: string): Promise<SlotResponse> {
  return api.get<SlotResponse>(`/slots/${slotId}`)
}

export function deleteSlot(slotId: string): Promise<void> {
  return api.delete(`/slots/${slotId}`)
}

// ==================== Session Allowed Transitions ====================

export interface AllowedTransitionsResponse {
  current_status: string
  allowed_transitions: string[]
}

export function getSessionAllowedTransitions(id: string): Promise<AllowedTransitionsResponse> {
  return api.get<AllowedTransitionsResponse>(`/sessions/${id}/allowed-transitions`)
}

// ==================== Session Waitlist ====================

export interface WaitlistEntry {
  enrollment_id: string
  enrollment_number: string
  contact_id: string
  position: number
  added_at: string
}

export function getSessionWaitlist(id: string): Promise<WaitlistEntry[]> {
  return api.get<WaitlistEntry[]>(`/sessions/${id}/waitlist`)
}

// ==================== Registrations ====================

interface ListRegistrationsParams {
  page?: number
  per_page?: number
  session_id?: string
  contact_id?: string
  status?: string
  /** Formations terminées sans attestation de fin de formation (migration 405). */
  attestation_pending?: boolean
  /** Codes de statut séparés par des virgules (au plus 20). */
  status_in?: string
  /** `true` : inscriptions actives, qui émargent (définition serveur
   *  `registration_status_is_active`) ; `false` : les autres. */
  active?: boolean
  /** Coupée à 100 caractères par le serveur. */
  search?: string
  /** `true` : l'enquête de la session leur est due.
   *  Règle `session_survey_owed_to_learner` (migration 1210). */
  survey_owed?: boolean
}

/** Filtres des cartes de tâches de l'accueil (migration 734). */
type PendingTaskFilters = Partial<Record<"convention_pending" | "convocation_pending", boolean>>

export function listRegistrations(params: ListRegistrationsParams & PendingTaskFilters = {}): Promise<PaginatedResponse<Registration>> {
  return api.get<Record<string, unknown>>("/registrations", params as Record<string, unknown>)
    .then((response) => transformListResponse<Registration>(response, "registrations"))
}

export interface RegistrationPipelineParams {
  stage?: RegistrationPipelineStageCode
  search?: string
  page?: number
  per_page?: number
}

/**
 * Pipeline d'inscription : compteurs de chaque étape sur toutes les
 * inscriptions de l'organisme, et page d'inscriptions de l'étape demandée.
 * L'étape est calculée par le serveur.
 */
export function getRegistrationPipeline(params: RegistrationPipelineParams = {}): Promise<RegistrationPipeline> {
  return api.get<RegistrationPipeline>("/registration-pipeline", params as Record<string, unknown>)
}

/**
 * Participants émargeables d'une session : inscriptions actives (`active=true`,
 * définition serveur `registration_status_is_active`), toutes pages chargées
 * (100 par page, plafond de `fetchAllPages`).
 */
export function listActiveSessionRegistrations(sessionId: string): Promise<AllPages<Registration>> {
  return fetchAllPages((page) =>
    api
      .get<Record<string, unknown>>("/registrations", {
        session_id: sessionId,
        active: true,
        page,
        per_page: 100,
      })
      .then((response) => transformListResponse<Registration>(response, "registrations"))
      .then((response) => ({ items: response.data, totalPages: response.pagination.total_pages })),
  )
}

/**
 * Toutes les inscriptions d'une session, quel que soit leur statut, page par
 * page (100 par page, plafond de `fetchAllPages`) : la fiche session compte et
 * liste ses inscrits, une liste coupée à 20 faussait les compteurs.
 */
export function listAllSessionRegistrations(sessionId: string): Promise<AllPages<Registration>> {
  return fetchAllPages((page) =>
    listRegistrations({ session_id: sessionId, page, per_page: 100 })
      .then((response) => ({ items: response.data, totalPages: response.pagination.total_pages })),
  )
}

/** Inscriptions proposées par recherche : au-delà, l'utilisateur affine sa saisie. */
export const REGISTRATION_SEARCH_PAGE_SIZE = 50

/**
 * Inscriptions actives (`active=true`, définition serveur) qui répondent à une
 * recherche serveur (participant, session) : le choix d'une inscription ne
 * dépend plus des 100 plus récentes.
 */
export function searchActiveRegistrations(search: string): Promise<PaginatedResponse<Registration>> {
  const term = search.trim().slice(0, 100)
  return listRegistrations({
    active: true,
    per_page: REGISTRATION_SEARCH_PAGE_SIZE,
    ...(term === "" ? {} : { search: term }),
  })
}

// comply-ignore: api-branded-id-types — les services du dépôt prennent des identifiants chaîne.
export function getRegistration(id: string): Promise<RegistrationDetail> {
  return api.get<RegistrationDetail>(`/registrations/${id}`)
}

export function createRegistration(data: Partial<Registration>): Promise<Registration> {
  return api.post<Registration>("/registrations", data)
}

export function updateRegistration(id: string, data: Partial<Registration>): Promise<Registration> {
  return api.put<Registration>(`/registrations/${id}`, data)
}

export interface TraineeStatus {
  id: string
  code: string
  label: string
  bpf_category: string
}

export function listTraineeStatuses(): Promise<TraineeStatus[]> {
  return api.get<TraineeStatus[]>("/ref/trainee-statuses")
}

export interface TransitionRegistrationRequest {
  target_status: string
  cancellation_reason?: string
  force?: boolean
}

export interface TransitionRegistrationResponse {
  success: boolean
  from_status: string
  to_status: string
  messages: string[]
  timestamp: string
  /** Passage en abandon : dossier d'abandon ouvert (ABD-…), `null` sinon. */
  dropout_case: { id: string; reference: string } | null
}

export function transitionRegistration(id: string, data: TransitionRegistrationRequest): Promise<TransitionRegistrationResponse> {
  return api.post<TransitionRegistrationResponse>(`/registrations/${id}/transition`, data)
}

export function deleteRegistration(id: string): Promise<void> {
  return api.delete(`/registrations/${id}`)
}

/** Garde non satisfaite, avec le lien qui la lève. */
export type TransitionBlocker = {
  code: string
  message: string
  fix_url: string | null
}

export interface ValidTransitionsResponse {
  current_status: string
  valid_transitions: string[]
  /** Blocages par cible, absente si acceptée. */
  blocked_by: Record<string, TransitionBlocker[]>
}

export function getRegistrationTransitions(id: string): Promise<ValidTransitionsResponse> {
  return api.get<ValidTransitionsResponse>(`/registrations/${id}/transitions`)
}

export interface WaitlistPositionResponse {
  registration_id: string
  position: number | null
}

export function getWaitlistPosition(id: string): Promise<WaitlistPositionResponse> {
  return api.get<WaitlistPositionResponse>(`/registrations/${id}/waitlist-position`)
}

export function autoConfirmRegistration(id: string): Promise<TransitionRegistrationResponse | null> {
  return api.post<TransitionRegistrationResponse | null>(`/registrations/${id}/auto-confirm`, {})
}

// ==================== Attendance ====================

/** Feuille d'émargement d'une liste — miroir de `AttendanceSheetListItem` (`attendance/sheets.rs`). */
export interface AttendanceSheetListItem {
  id: string
  session_id: string
  slot_id: string | null
  sheet_date: string
  /** Code `ref_slot_period` : `am`, `pm` ou `full`. */
  slot_period: string
  scheduled_start_time: string | null
  scheduled_end_time: string | null
  instructor_id: string | null
  location_name: string | null
  is_remote: boolean
  is_finalized: boolean
  finalized_at: string | null
  created_at: string
}

/** Page d'une liste paginée de l'émargement (`{ <clé>, total, per_page }`) → page de `fetchAllPages`. */
export function attendanceListPage<T>(
  response: { total: number; per_page: number },
  items: T[],
): FetchedPage<T> {
  return { items, totalPages: Math.ceil(response.total / Math.max(response.per_page, 1)) }
}

/**
 * Toutes les feuilles d'une session, page par page (100 par page, plafond de
 * `fetchAllPages`) : une feuille ancienne hors de la 1re page doit rester
 * visible, et la première demi-journée libre se calcule sur toutes.
 */
export function listAllAttendanceSheets(sessionId: string): Promise<AllPages<AttendanceSheetListItem>> {
  return fetchAllPages((page) =>
    api
      .get<{ sheets: AttendanceSheetListItem[]; total: number; per_page: number }>("/attendance/sheets", {
        session_id: sessionId,
        page,
        per_page: 100,
      })
      .then((response) => attendanceListPage(response, response.sheets)),
  )
}

/**
 * Détail d'une feuille d'émargement.
 * Miroir de `AttendanceSheetDetailResponse` (`src/api/handlers/attendance/`).
 * Les champs optionnels du backend (`Option<T>` sérialisés en `null`).
 * Ils sont typés `T | null`, jamais `T?`.
 * La frontière API porte l'absence explicitement.
 */
export interface AttendanceSheetDetail {
  id: string
  session_id: string
  slot_id: string | null
  sheet_date: string
  slot_period: string
  scheduled_start_time: string | null
  scheduled_end_time: string | null
  actual_start_time: string | null
  actual_end_time: string | null
  instructor_id: string | null
  /** Signature du formateur en data URL PNG, règle de Qualneo. `null` avant sa capture. */
  instructor_signature_url: string | null
  /** Signature du formateur que le relevé reproduit (image PNG ou
   *  `typed:<nom>`) : seule elle permet de finaliser une feuille signée électroniquement. */
  instructor_signature_valid: boolean
  instructor_signed_at: string | null
  location_name: string | null
  location_address: string | null
  is_remote: boolean
  remote_platform: string | null
  remote_meeting_url: string | null
  is_finalized: boolean
  finalized_at: string | null
  pdf_url: string | null
  pdf_generated_at: string | null
  notes: string | null
  /**
   * Horodatage de l'envoi automatique des liens d'émargement par le poller
   * d'auto-émargement. `null` tant que l'OF n'a pas activé l'auto-envoi ou que
   * le créneau n'a pas encore été atteint — le bouton d'envoi manuel reste
   * alors disponible.
   */
  auto_sent_at: string | null
  created_at: string
  updated_at: string
  /** Scan de la feuille signée (feuille papier), `null` tant qu'aucun n'est joint. */
  scan: AttendanceSheetScan | null
  /**
   * La feuille porte au moins un émargement papier : son scan est exigé pour
   * la finaliser (422 ATTENDANCE_SCAN_REQUIRED) et tient lieu de signature du
   * formateur.
   */
  has_paper_records: boolean
  /**
   * Date de l'effacement des preuves de la session, `null` sinon.
   * Posée, plus aucun scan (409 SESSION_EVIDENCE_PURGED).
   */
  evidence_purged_at: string | null
  /**
   * Source des plages comptées, dans l'ordre de la vue.
   * Créneau, horaires prévus, heures réelles, ou aucune.
   */
  effective_range_source: EffectiveRangeSource
  /** Plages comptées (« 09:00 – 12:30 »), dans l'ordre ; vide sans plage. */
  effective_ranges: string[]
}

/** Source des plages comptées d'une feuille (`vw_attendance_sheet_intervals.range_source`). */
export type EffectiveRangeSource = "slot" | "sheet" | "actual" | "none"

export function getAttendanceSheet(id: string): Promise<AttendanceSheetDetail> {
  return api.get<AttendanceSheetDetail>(`/attendance/sheets/${id}`)
}

export interface SignAttendanceSheetInstructorData {
  /** Data URL PNG produite par le SignaturePad. */
  signature_url: string
}

/**
 * Enregistre la signature du formateur sur une feuille d'émargement
 * (règle de Qualneo, exigée avant finalisation).
 * Le header CSRF `X-Requested-With` est ajouté par le client API.
 */
export function signAttendanceSheetInstructor(
  sheetId: string,
  data: SignAttendanceSheetInstructorData,
): Promise<AttendanceSheetDetail> {
  return api.post<AttendanceSheetDetail>(`/attendance/sheets/${sheetId}/sign-instructor`, data)
}

export interface CreateAttendanceSheetData {
  session_id: string
  sheet_date: string
  slot_period_code: string
  /**
   * Horaires prévus, saisis quand la session n'a aucun créneau pour cette
   * demi-journée : sans eux, la feuille n'a pas de durée (422
   * ATTENDANCE_DURATION_UNKNOWN à la finalisation).
   */
  scheduled: O.Option<ScheduledTimes>
}

export function createAttendanceSheet(data: CreateAttendanceSheetData): Promise<AttendanceSheetDetail> {
  return api.post<AttendanceSheetDetail>("/attendance/sheets", {
    session_id: data.session_id,
    sheet_date: data.sheet_date,
    slot_period_code: data.slot_period_code,
    ...pipe(
      data.scheduled,
      O.match(
        () => ({}),
        (times) => ({ scheduled_start_time: times.start, scheduled_end_time: times.end }),
      ),
    ),
  })
}

/**
 * Horaires prévus d'une feuille non finalisée : ils lui donnent sa durée
 * quand aucun créneau de la session ne couvre sa date et sa période, y
 * compris quand le planning de la date est figé par une autre feuille
 * finalisée (422 ATTENDANCE_SHEET_SCHEDULE_LOCKED une fois finalisée).
 */
export function setAttendanceSheetScheduledTimes(sheetId: string, times: ScheduledTimes): Promise<AttendanceSheetDetail> {
  return api.put<AttendanceSheetDetail>(`/attendance/sheets/${sheetId}/scheduled-times`, {
    scheduled_start_time: times.start,
    scheduled_end_time: times.end,
  })
}

export function finalizeAttendanceSheet(id: string): Promise<void> {
  return api.post(`/attendance/sheets/${id}/finalize`, {})
}

/** Plafond du motif de réouverture, celui du serveur. */
export const ATTENDANCE_REOPEN_REASON_MAX_CHARS = 500

/**
 * Rouvre une feuille finalisée (Administration › Modifier), motif journalisé.
 * 422 `ATTENDANCE_SHEET_NOT_FINALIZED` ou `ATTENDANCE_REOPEN_REASON_REQUIRED`.
 */
export function reopenAttendanceSheet(id: string, reason: string): Promise<AttendanceSheetDetail> {
  return api.post<AttendanceSheetDetail>(`/attendance/sheets/${id}/reopen`, { reason })
}

/**
 * Requêtes qui lisent les heures des feuilles finalisées.
 * Finaliser ou rouvrir une feuille les rafraîchit toutes.
 * Toute page qui lit ces heures s'ajoute ici.
 *
 * @example
 * invalidates: () => [...ATTENDANCE_HOURS_QUERY_KEYS]
 * // => [["registrations"], ["registration"], …, ["dashboard-stats"]]
 */
export const ATTENDANCE_HOURS_QUERY_KEYS: string[][] = [
  ["registrations"],
  ["registration"],
  ["attendance-summaries"],
  ["attendance-records"],
  ["bpf-report"],
  ["dropouts"],
  ["dropout"],
  ["dashboard-stats"],
]

/**
 * Toutes les présences d'une feuille, page par page (100 par page, plafond de
 * `fetchAllPages`) : au-delà de la 1re page, un stagiaire déjà émargé
 * paraîtrait « Nouveau » et sa ligne serait recréée en doublon.
 */
export function listAllAttendanceRecords(sheetId: string): Promise<AllPages<AttendanceRecord>> {
  return fetchAllPages((page) =>
    api
      .get<{ records: AttendanceRecord[]; total: number; per_page: number }>(`/attendance/sheets/${sheetId}/records`, {
        page,
        per_page: 100,
      })
      .then((response) => attendanceListPage(response, response.records)),
  )
}

// Noms API du retard et de l'heure de départ d'une ligne d'émargement, en
// lecture (liste) comme en écriture (création, mise à jour). Seul endroit à
// toucher si le backend les renomme : le reste de l'écran passe par
// `recordLateMinutes`, `recordDepartureTime` et les payloads ci-dessous.
const LATE_FIELD = "late_duration_minutes" as const
const DEPARTURE_FIELD = "check_out_time" as const

/** Ligne d'émargement — miroir de `AttendanceRecordListItem` (`attendance/records.rs`). */
export interface AttendanceRecord {
  id: string
  registration_id: string
  contact_id: string
  participant_name: string
  /** Code `ref_attendance_status`. */
  status: string
  check_in_time: string | null
  /** Heure de départ saisie (`HH:MM:SS`), déduite de la durée du créneau. */
  [DEPARTURE_FIELD]: string | null
  /** Retard saisi en minutes, déduit de la durée du créneau. */
  [LATE_FIELD]: number | null
  /** Minutes établies par l'émargement (vue des heures suivies). */
  actual_duration_minutes: number | null
  /** Horodatage de la saisie ; il n'établit pas la signature (`signature_valid`). */
  signed_at: string | null
  /**
   * Signature reproductible dans le relevé.
   * Règle de la finalisation et du dossier de preuves.
   * Une présence papier s'établit par le scan.
   */
  signature_valid: boolean
  /** Code `ref_attendance_signature_method` (`paper`, `digital_drawing`…). */
  signature_method: string | null
  internal_rules_acknowledged: boolean
  /**
   * Inscription encore active. Devenue inactive (abandon, annulée, liste
   * d'attente, en attente) ou supprimée, elle ne reçoit plus de lien de
   * signature, mais peut signer sa présence existante par le QR code.
   */
  registration_is_active: boolean
}

/**
 * Présence signée : signature reproductible.
 * Ou émargement papier saisi, établi par le scan.
 *
 * @example
 * recordIsSigned({ ...record, signature_valid: true }) // true
 * recordIsSigned({ ...record, signed_at: "2026-10-01T09:00:00Z", signature_method: "qr_code" }) // false
 */
export function recordIsSigned(record: AttendanceRecord): boolean {
  return record.signature_valid || (record.signature_method === "paper" && O.isSome(O.fromNullable(record.signed_at)))
}

/**
 * Signature électronique horodatée, sans image reproductible.
 * Sur une session purgée, la conservation l'a effacée.
 *
 * @example
 * recordSignatureErased({ ...record, signed_at: "2026-10-01T09:00:00Z", signature_method: "qr_code", signature_valid: false }) // true
 * recordSignatureErased({ ...record, signed_at: null, signature_valid: false }) // false
 */
export function recordSignatureErased(record: AttendanceRecord): boolean {
  const electronic = record.signature_method !== "paper"
  return !recordIsSigned(record) && electronic && O.isSome(O.fromNullable(record.signed_at))
}

export function recordLateMinutes(record: AttendanceRecord): O.Option<number> {
  return O.fromNullable(record[LATE_FIELD])
}

export function recordDepartureTime(record: AttendanceRecord): O.Option<string> {
  return O.fromNullable(record[DEPARTURE_FIELD])
}

export interface RecordAttendanceInput {
  registration_id: string
  contact_id: string
  update: AttendanceUpdate
  /** Présence émargée sur la feuille papier : son scan devra être joint. */
  paper: boolean
}

/** Retard et heure de départ d'une création : transmis seulement quand ils sont renseignés. */
function timingFields(update: AttendanceUpdate): Record<string, unknown> {
  const fields: Record<string, unknown> = {}
  pipe(
    update.check_out_time,
    O.map((time) => {
      fields[DEPARTURE_FIELD] = time
    }),
  )
  pipe(
    update.late_duration_minutes,
    O.map((minutes) => {
      fields[LATE_FIELD] = minutes
    }),
  )
  return fields
}

/** Crée la ligne d'émargement, retard et heure de départ compris. */
export function recordAttendance(sheetId: string, input: RecordAttendanceInput): Promise<{ id: string }> {
  return api.post<{ id: string }>(`/attendance/sheets/${sheetId}/records`, {
    registration_id: input.registration_id,
    contact_id: input.contact_id,
    status_code: input.update.status_code,
    ...timingFields(input.update),
    ...(input.paper ? { signature_method_code: "paper" } : {}),
  })
}

/**
 * Corps du PUT d'une ligne existante. Un champ que le statut porte et que
 * l'utilisateur a vidé part en `null` : le serveur efface la valeur
 * enregistrée (sinon un retard de 15 min vidé resterait en base et
 * continuerait d'amputer le certificat). Un champ que le statut ne porte pas
 * n'est pas transmis : le serveur l'efface de lui-même.
 *
 * `paper` = bascule du mode papier demandée (`none` : inchangé). Passer en
 * papier envoie `signature_method_code: "paper"` ; en sortir l'envoie à
 * `null`, et le stagiaire signe ensuite électroniquement.
 */
export function attendanceUpdateBody(update: AttendanceUpdate, paper: O.Option<boolean> = O.none): Record<string, unknown> {
  const body: Record<string, unknown> = { status_code: update.status_code }
  if (needsLateMinutes(update.status_code)) {
    body[LATE_FIELD] = O.toNullable(update.late_duration_minutes)
  }
  if (needsDepartureTime(update.status_code)) {
    body[DEPARTURE_FIELD] = O.toNullable(update.check_out_time)
  }
  pipe(
    paper,
    O.map((isPaper) => {
      body.signature_method_code = isPaper ? "paper" : null
    }),
  )
  return body
}

export function updateAttendance(id: string, update: AttendanceUpdate, paper: O.Option<boolean> = O.none): Promise<unknown> {
  return api.put(`/attendance/records/${id}`, attendanceUpdateBody(update, paper))
}

export interface SendSignatureLinksResponse {
  enqueued: number
  already_pending: number
  no_email: number
}

/**
 * Envoie les liens de signature de la feuille : à tous les inscrits actifs non
 * signés hors papier, ou aux seules inscriptions données (relance ciblée).
 * Refusé hors du jour de la feuille (ATTENDANCE_SIGN_WRONG_DAY).
 */
export function sendSignatureLinks(sheetId: string, registrationIds: O.Option<string[]> = O.none): Promise<SendSignatureLinksResponse> {
  const body = pipe(
    registrationIds,
    O.match(
      () => ({}),
      (ids) => ({ registration_ids: ids }),
    ),
  )
  return api.post<SendSignatureLinksResponse>(`/attendance/sheets/${sheetId}/send-signature-links`, body)
}

// ==================== Portail apprenant ====================

/**
 * Adresse postale d'un lieu de formation, exposée au portail apprenant (miroir
 * de la struct backend `PortalVenueAddress`). `latitude` / `longitude` sont des
 * NUMERIC sérialisés en chaîne (rust_decimal) : les coordonnées géographiques
 * du lieu. L'absence de lieu (formation à
 * distance) est modélisée par `venue: null` sur l'entité porteuse, jamais par
 * des champs plats vides. Source unique du type ; `portal.ts` le ré-exporte.
 */
export interface PortalVenueAddress {
  name: string
  address_line1: string
  address_line2: string | null
  postal_code: string
  city: string
  country: string
  latitude: string | null
  longitude: string | null
}

/** Résultat de l'envoi du lien d'accès, à mapper vers un toast dédié côté OF. */
export type GrantPortalEmailStatus =
  | "enqueued"
  | "already_pending"
  | "no_email"
  | "failed"
  | "not_requested"

export interface GrantPortalAccessResponse {
  id: string
  /** Lien magique en clair, affiché uniquement pour un « Copier le lien » à la
   * demande de l'organisme — jamais persisté ni journalisé côté client. */
  magic_link: string
  expires_at: string
  /** Sort de l'email d'accès : `enqueued` (mis en file), `already_pending` (déjà
   * en file, renvoi inutile), `no_email` (apprenant sans email), `failed` (jeton
   * créé mais email non mis en file). */
  email_status: GrantPortalEmailStatus
}

/**
 * Donne (ou renvoie) à un apprenant l'accès au portail sans mot de passe :
 * génère un jeton magic-link pour l'inscription et envoie le lien par email
 * (template `PORTAL_MAGIC_LINK`). Réutilise l'endpoint `POST /portal/access`
 * partagé avec les autres types de portail — l'idempotence de la file email
 * empêche le spam si le lien précédent est encore en attente d'envoi.
 */
export function grantPortalAccess(registrationId: string): Promise<GrantPortalAccessResponse> {
  return api.post<GrantPortalAccessResponse>(`/portal/access`, {
    entity_type: "LEARNER",
    entity_id: registrationId,
    expires_in_hours: 24,
    send_email: true,
  })
}

export interface QrTokenResponse {
  sign_url: string
  expires_at: string
}

export function createQrToken(sheetId: string): Promise<QrTokenResponse> {
  return api.post<QrTokenResponse>(`/attendance/sheets/${sheetId}/qr-token`, {})
}

export function revokeQrToken(sheetId: string): Promise<void> {
  return api.delete<void>(`/attendance/sheets/${sheetId}/qr-token`)
}

// ==================== Attendance Justifications ====================

export interface AttendanceJustification {
  id: string
  record_id: string
  justification_type_id: string
  justification_type: string
  /** Motif, détails, pièce et note de validation : `null` sans le droit
   *  Administration › Lire (donnée de santé possible), `restricted` vaut alors `true`. */
  reason: string | null
  details: string | null
  document_url: string | null
  document_name: string | null
  document_type: string | null
  document_size_bytes: number | null
  document_uploaded_at: string | null
  is_validated: boolean | null
  validated_at: string | null
  validated_by: string | null
  validation_notes: string | null
  valid_from: string | null
  valid_until: string | null
  submitted_by: string | null
  created_at: string
  updated_at: string
  restricted: boolean
}

export interface JustificationType {
  id: string
  code: string
  label: string
  description: string | null
  requires_document: boolean
  auto_validate: boolean
  validity_days: number | null
}

export function listJustifications(recordId: string): Promise<AttendanceJustification[]> {
  return api.get<AttendanceJustification[]>(`/attendance/records/${recordId}/justifications`)
}

export function createJustification(recordId: string, data: {
  justification_type_id: string
  reason: string
  details?: string
  document_url?: string
  document_name?: string
  valid_from?: string
  valid_until?: string
}): Promise<AttendanceJustification> {
  return api.post<AttendanceJustification>(`/attendance/records/${recordId}/justifications`, data)
}

export function validateJustification(id: string, data: { is_validated: boolean; validation_notes?: string }): Promise<AttendanceJustification> {
  return api.put<AttendanceJustification>(`/attendance/justifications/${id}/validate`, data)
}

export function listJustificationTypes(): Promise<JustificationType[]> {
  return api.get<JustificationType[]>("/attendance/justification-types")
}

// ==================== Attendance Summaries ====================

export interface AttendanceSummary {
  id: string
  registration_id: string
  session_id: string
  /** Intitulé de la session (code et programme). */
  session_title: string
  contact_id: string
  contact_name: string
  total_slots: number
  attended_slots: number
  absent_justified_slots: number
  absent_unjustified_slots: number
  late_slots: number
  total_scheduled_minutes: number
  total_attended_minutes: number
  total_late_minutes: number
  attendance_rate: string
  is_at_risk: boolean
  risk_alert_sent_at: string | null
  last_attendance_date: string | null
  last_calculated_at: string
  created_at: string
  updated_at: string
}

interface ListAttendanceSummariesParams {
  page?: number
  per_page?: number
  session_id?: string
  is_at_risk?: boolean
}

export function listAttendanceSummaries(params: ListAttendanceSummariesParams = {}): Promise<PaginatedResponse<AttendanceSummary>> {
  return api.get<Record<string, unknown>>("/attendance/summaries", params as Record<string, unknown>)
    .then((response) => transformListResponse<AttendanceSummary>(response, "summaries"))
}

export function recalculateSummaries(data: { session_id?: string } = {}): Promise<{ success: boolean; recalculated: number }> {
  return api.post<{ success: boolean; recalculated: number }>("/attendance/summaries/recalculate", data)
}

// ==================== Juries (Certifications) ====================

export interface Jury {
  id: string
  session_id: string
  scheduled_date: string
  scheduled_time: string
  venue_id: string
  venue_name: string
  room: string
  remote_url: string
  status_id: string
  status: string
  notes: string
  created_at: string
  updated_at: string
  /** Code de session, titre du programme ; `null` si absents. */
  session_code: string
  program_title: string
  /** Dernier PV stocké : id et numéro ; `null` sans PV. */
  last_pv_document_id: string
  last_pv_document_number: string
}

/** Jurys par statut. Filtres de la liste, hors statut. */
export type JuryStatusCount = {
  code: string
  label: string
  count: number
}

export interface JuryListResponse extends PaginatedResponse<Jury> {
  status_counts: JuryStatusCount[]
}

export interface JuryMember {
  id: string
  jury_id: string
  contact_id: string
  user_id: string
  contact_name: string
  role_id: string
  role: string
  /** Libellé du rôle, à afficher (« Président », « Secrétaire »). */
  role_label: string
  invitation_sent_at: string
  confirmed_at: string
  declined_at: string
  attended: boolean
  attendance_notes: string
  created_at: string
}

export interface JuryMemberRole {
  id: string
  code: string
  label: string
}

export interface CreateJuryRequest {
  session_id: string
  scheduled_date: string
  scheduled_time?: string
  venue_id?: string
  room?: string
  remote_url?: string
  notes?: string
}

export interface UpdateJuryRequest {
  scheduled_date?: string
  scheduled_time?: string
  venue_id?: string
  room?: string
  remote_url?: string
  notes?: string
}

export interface AddJuryMemberRequest {
  contact_id: string
  role_id: string
}

type ListJuriesParams = {
  page?: number
  per_page?: number
  session_id?: string
  status?: string
  from_date?: string
  to_date?: string
}

export function listJuries(params: ListJuriesParams = {}): Promise<JuryListResponse> {
  // comply-ignore: api-route-version-prefix — le client `api` préfixe déjà /api/v1.
  return api.get<JuryListResponse>("/juries", params)
}

export function getJury(id: string): Promise<Jury> {
  return api.get<Jury>(`/juries/${id}`)
}

export function createJury(data: CreateJuryRequest): Promise<Jury> {
  return api.post<Jury>("/juries", data)
}

export function updateJury(id: string, data: UpdateJuryRequest): Promise<Jury> {
  return api.put<Jury>(`/juries/${id}`, data)
}

export function deleteJury(id: string): Promise<void> {
  return api.delete(`/juries/${id}`)
}

export function transitionJuryStatus(id: string, data: { to_status: string; notes?: string }): Promise<Jury> {
  return api.post<Jury>(`/juries/${id}/transition`, data)
}

export function listJuryMembers(juryId: string): Promise<JuryMember[]> {
  return api.get<JuryMember[]>(`/juries/${juryId}/members`)
}

export function addJuryMember(juryId: string, data: AddJuryMemberRequest): Promise<JuryMember> {
  return api.post<JuryMember>(`/juries/${juryId}/members`, data)
}

export function removeJuryMember(juryId: string, memberId: string): Promise<void> {
  return api.delete(`/juries/${juryId}/members/${memberId}`)
}

export function listJuryMemberRoles(): Promise<JuryMemberRole[]> {
  return api.get<JuryMemberRole[]>("/jury-member-roles")
}

// =============================================================================
// Jury Portal - Token Management & Grading (Protected endpoints)
// =============================================================================

export interface SendJuryInvitationsRequest {
  member_ids?: string[]
  token_valid_days?: number
}

export interface SendJuryInvitationsResponse {
  sent: number
  failed: number
  message: string
}

export interface GradeStatus {
  id: string
  code: string
  label: string
  color: string
  is_passing: boolean
  requires_comment: boolean
  sort_order: number
}

export interface MemberGrade {
  member_id: string
  member_name: string
  grade_status_code: string | null
  grade_status_label: string | null
  is_passing: boolean | null
  comment: string | null
  graded_at: string | null
}

export interface DeliberationInfo {
  final_grade_status_code: string
  final_grade_status_label: string
  final_score_numeric: number | null
  deliberation_notes: string | null
  conditions: string | null
  validated_at: string | null
  validated_by_name: string | null
}

export interface CandidateGradesSummary {
  registration_id: string
  contact_name: string
  grades: MemberGrade[]
  deliberation: DeliberationInfo | null
}

export interface JuryMemberSummary {
  id: string
  name: string
  role: string
  /** Libellé du rôle, à afficher. */
  role_label: string
  is_president: boolean
  grades_submitted: number
  portal_access_count: number
  last_access: string | null
}

export interface ConsolidatedGradesResponse {
  candidates: CandidateGradesSummary[]
  jury_members: JuryMemberSummary[]
}

export interface SaveDeliberationRequest {
  final_grade_status_code: string
  final_score_numeric?: number
  deliberation_notes?: string
  conditions?: string
}

export function sendJuryInvitations(juryId: string, data: SendJuryInvitationsRequest = {}): Promise<SendJuryInvitationsResponse> {
  return api.post<SendJuryInvitationsResponse>(`/juries/${juryId}/send-invitations`, data)
}

export function listGradeStatuses(): Promise<GradeStatus[]> {
  return api.get<GradeStatus[]>("/jury-grade-statuses")
}

export function getConsolidatedGrades(juryId: string): Promise<ConsolidatedGradesResponse> {
  return api.get<ConsolidatedGradesResponse>(`/juries/${juryId}/grades`)
}

export function saveDeliberation(juryId: string, registrationId: string, data: SaveDeliberationRequest): Promise<{ success: boolean; message: string }> {
  return api.post(`/juries/${juryId}/candidates/${registrationId}/deliberation`, data)
}

// =============================================================================
// Jury PV (Procès-Verbal) Generation
// =============================================================================

export interface GeneratedPvResponse {
  id: string
  document_number: string
  title: string
  document_type: string
  status: string
  generated_at: string
}

export function generateJuryPV(juryId: string): Promise<GeneratedPvResponse> {
  return api.post<GeneratedPvResponse>(`/juries/${juryId}/generate-pv`, {})
}

// =============================================================================
// Jury Portal - Public endpoints (accessed via token, no auth required)
// =============================================================================

export interface JuryPortalSession {
  jury_id: string
  session_ref: string
  program_name: string
  scheduled_date: string
  scheduled_time: string | null
  venue_name: string | null
  room: string | null
  remote_url: string | null
  member_name: string
  member_role: string
  /** Libellé du rôle du juré, à afficher. */
  member_role_label: string
  is_president: boolean
  candidates_count: number
}

export interface GradeInfo {
  grade_status_code: string
  grade_status_label: string
  is_passing: boolean
  score_numeric: number | null
  comment: string | null
  graded_at: string
}

export type JuryCandidate = {
  registration_id: string
  contact_name: string
  has_been_graded: boolean
  my_grade: GradeInfo | null
}

export type SubmitGradeRequest = {
  grade_status_code: string
  score_numeric?: number
  comment?: string
  private_note?: string
  competency_block_id?: string
}

// Public API (no authentication, accessed via token)

/**
 * Refus du serveur sur une route publique, lisible par `translateError`.
 * `response.data` porte le Problem JSON, avec son `code`.
 * Le portail affiche ainsi le refus traduit, jamais « Request failed ».
 */
export class PublicApiError extends Error {
  override readonly name = "PublicApiError"
  readonly response: { status: number; data: unknown }

  constructor(status: number, problem: unknown) {
    super(`HTTP ${status}`)
    this.response = { status, data: problem }
  }
}

/**
 * Corps JSON d'une réponse publique, ou son refus.
 *
 * @template T Corps attendu d'une réponse réussie.
 */
function publicJson<T>(response: Response): Promise<T> {
  return response.ok
    ? (response.json() as Promise<T>)
    : response.text().then((text) => {
        const problem = pipe(
          J.parse(text),
          E.getOrElseW(() => ({})),
        )
        throw new PublicApiError(response.status, problem)
      })
}

/** Adresse d'une route publique de l'API. */
function publicApiUrl(path: string): string {
  const base = pipe(
    O.fromNullable(import.meta.env.VITE_API_URL),
    O.getOrElse(() => ""),
  )
  return `${base}/api/v1${path}`
}

// Un appel `fetch` par verbe, en-têtes en clair.
// La garde CSRF (csrf-header-guard.test.ts) lit chaque appel.
const publicApi = {
  get: <T>(url: string): Promise<T> =>
    fetch(publicApiUrl(url), {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    }).then((response) => publicJson<T>(response)),
  post: <T>(url: string, data?: unknown): Promise<T> =>
    fetch(publicApiUrl(url), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        // Le portail jury n'est pas exempté du contrôle CSRF : sans ce header,
        // la note du jury externe part en 403 « CSRF header manquant ».
        "X-Requested-With": "XMLHttpRequest",
      },
      body: data ? JSON.stringify(data) : undefined,
    }).then((response) => publicJson<T>(response)),
}

export function validateJuryToken(token: string): Promise<JuryPortalSession> {
  return publicApi.get<JuryPortalSession>(`/jury/access/${token}`)
}

export function getJuryCandidates(token: string): Promise<JuryCandidate[]> {
  return publicApi.get<JuryCandidate[]>(`/jury/access/${token}/candidates`)
}

export function submitJuryGrade(token: string, registrationId: string, data: SubmitGradeRequest): Promise<{ success: boolean; message: string }> {
  return publicApi.post(`/jury/access/${token}/candidates/${registrationId}/grade`, data)
}

export function getPublicGradeStatuses(token: string): Promise<GradeStatus[]> {
  return publicApi.get<GradeStatus[]>(`/jury/access/${token}/grade-statuses`)
}

// =============================================================================
// Program Deliverables (Livrables de programme - §5.5)
// =============================================================================

export interface DeliverableType {
  id: string
  code: string
  label: string
  description: string | null
  icon: string | null
  color: string
}

export interface ResponsibleRole {
  id: string
  code: string
  label: string
}

export interface ProgramDeliverable {
  id: string
  title: string
  description: string | null
  deliverable_type: string
  deliverable_type_label: string
  deliverable_type_color: string
  responsible_role: string
  responsible_role_label: string
  module_id: string | null
  module_title: string | null
  is_mandatory: boolean
  due_timing: string | null
  due_days_before: number | null
  sort_order: number
}

export interface ListDeliverablesResponse {
  deliverables: ProgramDeliverable[]
  total: number
}

export interface CreateDeliverableRequest {
  title: string
  description?: string
  deliverable_type_id: string
  responsible_role_id: string
  module_id?: string
  is_mandatory?: boolean
  due_timing?: string
  due_days_before?: number
  sort_order?: number
  resource_id?: string
}

export interface UpdateDeliverableRequest {
  title?: string
  description?: string
  deliverable_type_id?: string
  responsible_role_id?: string
  module_id?: string
  is_mandatory?: boolean
  due_timing?: string
  due_days_before?: number
  sort_order?: number
  resource_id?: string
}

export function listDeliverableTypes(): Promise<DeliverableType[]> {
  return api.get<DeliverableType[]>("/deliverable-types")
}

export function listResponsibleRoles(): Promise<ResponsibleRole[]> {
  return api.get<ResponsibleRole[]>("/responsible-roles")
}

export function listProgramDeliverables(programId: string): Promise<ListDeliverablesResponse> {
  return api.get<ListDeliverablesResponse>(`/programs/${programId}/deliverables`)
}

export function createProgramDeliverable(programId: string, data: CreateDeliverableRequest): Promise<ProgramDeliverable> {
  return api.post<ProgramDeliverable>(`/programs/${programId}/deliverables`, data)
}

export function updateProgramDeliverable(programId: string, deliverableId: string, data: UpdateDeliverableRequest): Promise<ProgramDeliverable> {
  return api.put<ProgramDeliverable>(`/programs/${programId}/deliverables/${deliverableId}`, data)
}

export function deleteProgramDeliverable(programId: string, deliverableId: string): Promise<void> {
  return api.delete(`/programs/${programId}/deliverables/${deliverableId}`)
}

// =============================================================================
// Program Public Links (Liens publics - §5.6)
// =============================================================================

export interface ProgramPublicLink {
  id: string
  token: string
  url: string
  has_password: boolean
  expires_at: string | null
  is_active: boolean
  view_count: number
  last_viewed_at: string | null
  created_at: string
}

export interface CreatePublicLinkRequest {
  password?: string
  expires_at?: string
  version_id?: string
}

export function listProgramPublicLinks(programId: string): Promise<ProgramPublicLink[]> {
  return api.get<ProgramPublicLink[]>(`/programs/${programId}/public-links`)
}

export function createProgramPublicLink(programId: string, data: CreatePublicLinkRequest = {}): Promise<ProgramPublicLink> {
  return api.post<ProgramPublicLink>(`/programs/${programId}/public-links`, data)
}

export function revokeProgramPublicLink(programId: string, linkId: string): Promise<void> {
  return api.delete(`/programs/${programId}/public-links/${linkId}`)
}

// =============================================================================
// Program Export (Export PDF - §5.6)
// =============================================================================

export interface ProgramExportRequest {
  id: string
  export_format: string
  status: "pending" | "processing" | "completed" | "failed"
  file_path: string | null
  file_size: number | null
  error_message: string | null
  /**
   * URL présignée de téléchargement (TTL 1 h), dérivée côté serveur à la
   * lecture du statut détail quand `status === "completed"`. `null` tant que
   * l'export n'est pas terminé, ou en échec. Jamais renvoyée par la liste.
   */
  download_url: string | null
  requested_at: string
  completed_at: string | null
}

export interface CreateExportRequest {
  export_format?: "full" | "summary" | "learner" | "audit"
  include_competency_matrix?: boolean
  include_resources_list?: boolean
}

export function listProgramExportRequests(programId: string): Promise<ProgramExportRequest[]> {
  return api.get<ProgramExportRequest[]>(`/programs/${programId}/exports`)
}

export function createProgramExportRequest(programId: string, data: CreateExportRequest = {}): Promise<ProgramExportRequest> {
  return api.post<ProgramExportRequest>(`/programs/${programId}/exports`, data)
}

export function getProgramExportRequest(programId: string, requestId: string): Promise<ProgramExportRequest> {
  return api.get<ProgramExportRequest>(`/programs/${programId}/exports/${requestId}`)
}

// =============================================================================
// Public Program Access (via token, no auth)
// =============================================================================

export interface PublicProgramModule {
  code: string
  title: string
  description: string | null
  duration_hours: number
  sort_order: number
}

export interface PublicProgramObjective {
  id: string
  objective_type: string
  description: string
  sort_order: number
}

export interface PublicProgramPrerequisite {
  id: string
  description: string
  is_mandatory: boolean
  verification_method: string | null
  sort_order: number
}

export interface PublicProgram {
  id: string
  code: string
  title: string
  short_description: string | null
  full_description: string | null
  program_type: string
  certification_type: string | null
  rncp_code: string | null
  duration_hours: number
  duration_days: number | null
  prerequisites: string | null
  target_audience: string | null
  teaching_methods: string | null
  assessment_methods: string | null
  accessibility_info: string | null
  access_delay_days: number | null
  access_modalities: string | null
  /** Tarifs catalogue HT par modalité (null = sur devis). Qualiopi Ind. 1. */
  price_inter: number | null
  price_intra: number | null
  price_individual: number | null
  /** Mode de tarification : forfait | hourly | daily. Quand hourly/daily, afficher hourly_rate_ht €/h ou daily_rate_ht €/j. */
  pricing_mode: "forfait" | "hourly" | "daily" | null
  hourly_rate_ht: number | null
  daily_rate_ht: number | null
  /** Modalités de financement déclarées (libellés). Qualiopi Ind. 1 au 1er novembre 2026. */
  financing_modalities: string[]
  /** Type de reconnaissance en libellé développé pour le public. Qualiopi Ind. 1 au 1er novembre 2026. */
  certification_type_label: CertificationTypeLabelWire
  objectives: PublicProgramObjective[]
  prerequisites_list: PublicProgramPrerequisite[]
  modules: PublicProgramModule[]
}

export function getPublicProgram(token: string, password?: string): Promise<PublicProgram> {
  return publicApi.post<PublicProgram>(`/public/programs/${token}`, { password })
}

// =============================================================================
// NSF Codes (Reference Data)
// =============================================================================

export interface NsfCode {
  id: string
  code: string
  label: string
  domain: string | null
}

export function listNsfCodes(q?: string): Promise<NsfCode[]> {
  const params: Record<string, string> = {}
  if (q) {
    params.q = q
  }
  return api.get<NsfCode[]>("/ref/nsf-codes", params)
}

// =============================================================================
// Coherence Check (AC-09)
// =============================================================================

export interface OrphanItem {
  id: string
  name: string
  description: string | null
}

export interface CoherenceCheckResponse {
  coherence_score: number
  total_objectives: number
  covered_objectives: number
  orphan_objectives: OrphanItem[]
  orphan_modules: OrphanItem[]
  objectives_without_evaluation: OrphanItem[]
  recommendations: string[]
}

export function getProgramCoherenceCheck(programId: string): Promise<CoherenceCheckResponse> {
  return api.get<CoherenceCheckResponse>(`/programs/${programId}/coherence-check`)
}

// =============================================================================
// Program Versions (Qualiopi - Ind. 32)
// =============================================================================

export interface VersionStatusItem {
  id: string
  code: string
  label: string
  description: string | null
  is_editable: boolean
  is_usable: boolean
  sort_order: number
}

export interface ChangeTypeItem {
  id: string
  code: string
  label: string
  description: string | null
  requires_validation: boolean
  sort_order: number
}

export interface ProgramVersionListItem {
  id: string
  program_id: string
  version_major: number
  version_minor: number
  version_patch: number
  version_label: string | null
  change_summary: string | null
  change_type_code: string
  change_type_label: string
  status_code: string
  status_label: string
  is_editable: boolean
  is_usable: boolean
  approved_at: string | null
  approved_by_name: string | null
  created_at: string
  created_by_name: string | null
}

export interface CreateProgramVersionRequest {
  version_major: number
  version_minor: number
  version_patch: number
  version_label?: string
  change_summary?: string
  change_type_id: string
  snapshot_modules?: boolean
  snapshot_skills?: boolean
}

export function listProgramVersions(programId: string): Promise<ProgramVersionListItem[]> {
  return api.get<ProgramVersionListItem[]>(`/programs/${programId}/versions`)
}

export function createProgramVersion(programId: string, data: CreateProgramVersionRequest): Promise<unknown> {
  return api.post(`/programs/${programId}/versions`, data)
}

export function listVersionStatuses(): Promise<VersionStatusItem[]> {
  return api.get<VersionStatusItem[]>("/version-statuses")
}

export function listChangeTypes(): Promise<ChangeTypeItem[]> {
  return api.get<ChangeTypeItem[]>("/change-types")
}

export function transitionProgramVersion(programId: string, versionId: string, targetStatusCode: string): Promise<unknown> {
  return api.post(`/programs/${programId}/versions/${versionId}/transition`, { target_status_code: targetStatusCode })
}

export function deleteProgramVersion(programId: string, versionId: string): Promise<void> {
  return api.delete(`/programs/${programId}/versions/${versionId}`)
}

// ==================== Dropouts ====================

interface ListDropoutsParams {
  page?: number
  per_page?: number
  status?: string
  /** Nom du stagiaire, intitulé ou code de session, référence ABD-… (100 caractères au plus). */
  search?: string
  /** Inscriptions visées, séparées par des virgules (100 au plus). */
  registration_ids?: string
  /** `true` : dossiers ouverts seulement. */
  open?: boolean
  /** Dossiers de la revue annuelle de cette année. */
  annual_review_year?: number
}

export function listDropouts(params: ListDropoutsParams = {}): Promise<PaginatedResponse<DropoutListItem>> {
  return api.get<Record<string, unknown>>("/dropouts", params as Record<string, unknown>)
    .then((response) => transformListResponse<DropoutListItem>(response, "dropouts"))
}

/**
 * Parmi `registrationIds`, celles qui ont un dossier d'abandon ouvert : le
 * choix d'une inscription à déclarer ne les propose pas (le serveur refuse un
 * second dossier ouvert, 409 DROPOUT_ALREADY_OPEN).
 */
export function listOpenDropoutRegistrationIds(registrationIds: ReadonlyArray<string>): Promise<Set<string>> {
  if (registrationIds.length === 0) return Promise.resolve(new Set())
  return listDropouts({
    open: true,
    registration_ids: registrationIds.slice(0, 100).join(","),
    per_page: 100,
  }).then((response) => new Set(response.data.map((dropout) => dropout.registration_id)))
}

/**
 * Statistiques d'abandon d'une année : l'année entière puis chaque mois.
 * Sans année, celle en cours dans le fuseau de l'organisme.
 */
export function getDropoutStatistics(year: O.Option<number>): Promise<DropoutStatistics[]> {
  return api.get<DropoutStatistics[]>(
    "/dropouts/statistics",
    pipe(
      year,
      O.match(
        () => ({}),
        (value) => ({ year: value }),
      ),
    ),
  )
}

/** Relances d'un dossier, de la plus récente à la plus ancienne. */
export function listDropoutReminders(id: string): Promise<DropoutReminder[]> {
  return api.get<DropoutReminder[]>(`/dropouts/${id}/reminders`)
}

/** Consigne une relance (rien n'est envoyé) : canal de l'organisme, objet et message facultatifs. */
export function createDropoutReminder(id: string, data: Record<string, string>): Promise<DropoutReminder> {
  return api.post<DropoutReminder>(`/dropouts/${id}/reminders`, data)
}

/** Issue d'une relance : réponse reçue, contact établi, compte rendu, suite à donner. */
export function updateDropoutReminderOutcome(
  id: string,
  reminderId: string,
  data: Record<string, string | boolean | null>,
): Promise<DropoutReminder> {
  return api.patch<DropoutReminder>(`/dropouts/${id}/reminders/${reminderId}`, data)
}

/** Types de pièce d'un dossier d'abandon (`ref_dropout_document_type`). */
export function listDropoutDocumentTypes(): Promise<DropoutRefOption[]> {
  return api.get<DropoutRefOption[]>("/dropouts/document-types")
}

/** Types d'abandon (`ref_dropout_type`) — `RefDataItem` de `dropouts/reference.rs`. */
export function listDropoutTypes(): Promise<DropoutRefOption[]> {
  return api.get<DropoutRefOption[]>("/dropouts/types")
}

/** Statuts d'abandon (`ref_dropout_status`) — `DropoutStatusItem` de `dropouts/reference.rs`. */
export function listDropoutStatuses(): Promise<DropoutStatusOption[]> {
  return api.get<DropoutStatusOption[]>("/dropouts/statuses")
}

/** Catégories de motif (`ref_dropout_reason_category`). */
export function listDropoutReasonCategories(): Promise<DropoutRefOption[]> {
  return api.get<DropoutRefOption[]>("/dropouts/reason-categories")
}

/** Méthodes de détection (`ref_detection_method`). */
export function listDropoutDetectionMethods(): Promise<DropoutRefOption[]> {
  return api.get<DropoutRefOption[]>("/dropouts/detection-methods")
}

export function getDropout(id: string): Promise<DropoutDetail> {
  return api.get<DropoutDetail>(`/dropouts/${id}`)
}

export function createDropout(data: Record<string, unknown>): Promise<DropoutDetail> {
  return api.post<DropoutDetail>("/dropouts", data)
}

export function updateDropout(id: string, data: Record<string, unknown>): Promise<DropoutDetail> {
  return api.put<DropoutDetail>(`/dropouts/${id}`, data)
}

/**
 * Clôture : `status_id` = statut final choisi (`is_final` de GET
 * /dropouts/statuses), notes et revue annuelle facultatives. « Abandon
 * confirmé » passe l'inscription en abandon, « Réintégré » la rend active.
 */
export function closeDropout(id: string, data: DropoutClosePayload): Promise<DropoutDetail> {
  return api.post<DropoutDetail>(`/dropouts/${id}/close`, data)
}

/** Types d'action préventive (`ref_preventive_action_type`). */
export function listDropoutPreventiveActionTypes(): Promise<DropoutRefOption[]> {
  return api.get<DropoutRefOption[]>("/dropouts/preventive-action-types")
}

/** Types d'action corrective (`ref_corrective_action_type`). */
export function listDropoutCorrectiveActionTypes(): Promise<DropoutRefOption[]> {
  return api.get<DropoutRefOption[]>("/dropouts/corrective-action-types")
}

/** Résultats d'action corrective (`ref_corrective_action_result`). */
export function listDropoutCorrectiveActionResults(): Promise<DropoutRefOption[]> {
  return api.get<DropoutRefOption[]>("/dropouts/corrective-action-results")
}

/** Résultats d'action préventive (`ref_preventive_action_result`). */
export function listDropoutPreventiveActionResults(): Promise<DropoutRefOption[]> {
  return api.get<DropoutRefOption[]>("/dropouts/preventive-action-results")
}

export function listDropoutCorrectiveActions(id: string): Promise<DropoutCorrectiveAction[]> {
  return api.get<DropoutCorrectiveAction[]>(`/dropouts/${id}/corrective-actions`)
}

export function createDropoutCorrectiveAction(id: string, data: Record<string, unknown>): Promise<DropoutCorrectiveAction> {
  return api.post<DropoutCorrectiveAction>(`/dropouts/${id}/corrective-actions`, data)
}

/** Exécution, résultat et efficacité d'une action corrective. */
export function updateDropoutCorrectiveAction(id: string, actionId: string, data: Record<string, unknown>): Promise<DropoutCorrectiveAction> {
  return api.put<DropoutCorrectiveAction>(`/dropouts/${id}/corrective-actions/${actionId}`, data)
}

export function listDropoutPreventiveActions(id: string): Promise<DropoutPreventiveAction[]> {
  return api.get<DropoutPreventiveAction[]>(`/dropouts/${id}/preventive-actions`)
}

export function createDropoutPreventiveAction(id: string, data: Record<string, unknown>): Promise<DropoutPreventiveAction> {
  return api.post<DropoutPreventiveAction>(`/dropouts/${id}/preventive-actions`, data)
}

/** Résultat d'une action préventive. */
export function updateDropoutPreventiveAction(id: string, actionId: string, data: Record<string, unknown>): Promise<DropoutPreventiveAction> {
  return api.put<DropoutPreventiveAction>(`/dropouts/${id}/preventive-actions/${actionId}`, data)
}

/** Pièces d'un dossier ; sans `admin.read`, nom et téléchargement restent masqués. */
export function listDropoutDocuments(id: string): Promise<DropoutDocument[]> {
  return api.get<DropoutDocument[]>(`/dropouts/${id}/documents`)
}

/** Ajout d'une pièce (multipart : `file`, `document_type`, `description`). */
export function uploadDropoutDocument(
  id: string,
  file: File,
  documentType: string,
  description: string,
): Promise<DropoutDocument> {
  const form = new FormData()
  form.append("file", file)
  if (documentType.trim() !== "") form.append("document_type", documentType.trim())
  if (description.trim() !== "") form.append("description", description.trim())
  return api.postForm<DropoutDocument>(`/dropouts/${id}/documents`, form)
}

// =============================================================================
// Enrollment Workflow (9-Step Qualiopi Process)
// =============================================================================

export interface EnrollmentStatusRef {
  id: string
  code: string
  label_fr: string
  label_en: string | null
  step_number: number
  workflow_phase: string | null
  color: string | null
  icon: string | null
  is_terminal: boolean
  is_success: boolean
}

export interface EnrollmentWorkflowDetail {
  id: string
  tenant_id: string
  session_id: string
  contact_id: string
  registration_id: string | null
  learner_id: string | null
  current_status_id: string
  enrollment_number: string
  enrollment_type: string
  information_requested_at: string | null
  needs_analysis_scheduled_at: string | null
  needs_analysis_completed_at: string | null
  quote_sent_at: string | null
  quote_accepted_at: string | null
  registration_confirmed_at: string | null
  convention_sent_at: string | null
  convention_signed_at: string | null
  summons_sent_at: string | null
  summons_acknowledged_at: string | null
  assessment_sent_at: string | null
  assessment_completed_at: string | null
  training_started_at: string | null
  training_completed_at: string | null
  hot_evaluation_at: string | null
  cold_evaluation_sent_at: string | null
  cold_evaluation_completed_at: string | null
  finalized_at: string | null
  financing_type: string | null
  opco_name: string | null
  total_amount: string | null
  funding_confirmed: boolean
  is_waitlisted: boolean
  waitlist_position: number | null
  priority_level: number
  needs_quote: boolean
  has_disability_accommodation: boolean
  is_cancelled: boolean
  is_abandoned: boolean
  is_rejected: boolean
  source: string | null
  internal_notes: string | null
  created_at: string
  updated_at: string
  status: EnrollmentStatusRef
}

export interface EnrollmentWorkflowSummary {
  id: string
  enrollment_number: string
  enrollment_type: string
  status_code: string
  status_label: string
  step_number: number
  workflow_phase: string | null
  status_color: string | null
  is_terminal: boolean
  is_waitlisted: boolean
  is_cancelled: boolean
  is_abandoned: boolean
  needs_quote: boolean
  funding_confirmed: boolean
  financing_type: string | null
  total_amount: string | null
  information_requested_at: string | null
  registration_confirmed_at: string | null
  convention_signed_at: string | null
  training_started_at: string | null
  training_completed_at: string | null
  finalized_at: string | null
  session_id: string
  contact_id: string
  /** « Prénom Nom » du contact, null s'il est supprimé. */
  contact_name: string | null
  created_at: string
}

export interface EnrollmentWorkflowTransition {
  from_status_code: string
  to_status_code: string
  to_status_label: string
  description: string | null
  is_automatic: boolean
}

export interface EnrollmentStepProgress {
  step_number: number
  step_name: string
  status: "completed" | "current" | "pending"
  completed_at: string | null
}

export interface EnrollmentWorkflowProgress {
  enrollment_id: string
  enrollment_number: string
  current_step: number
  steps: EnrollmentStepProgress[]
  progress_percentage: number
}

export interface EnrollmentHistoryEntry {
  id: string
  enrollment_id: string
  from_status_id: string | null
  to_status_id: string
  reason: string | null
  triggered_by: string
  triggered_by_user_id: string | null
  metadata: Record<string, unknown> | null
  transitioned_at: string
  /** Libellé du statut de départ, à lire avec `apiText`. */
  from_status_label: unknown
  /** Libellé du statut d'arrivée : à lire avec `apiText`. */
  to_status_label: unknown
  /** Auteur lisible : nom de l'utilisateur, sinon origine (« Automatique »…). */
  triggered_by_label: string
}

/** Type d'adaptation d'une analyse des besoins de parcours. */
export type EnrollmentAdaptationType = "disability" | "language" | "schedule" | "content" | "mixed"

/** Analyse des besoins d'un parcours, telle que servie par l'API. */
export interface EnrollmentNeedsAnalysis {
  id: string
  tenant_id: string
  enrollment_id: string
  needs_assessment_id: string | null
  company_name: string | null
  company_siret: string | null
  industry_sector: string | null
  company_size: string | null
  request_context: string
  business_objectives: string
  urgency_level: string | null
  learners_count: number | null
  learners_profiles: string | null
  identified_needs: string
  expected_skills: string
  success_criteria: string | null
  has_prerequisites: boolean
  prerequisites_met: boolean | null
  prerequisites_notes: string | null
  needs_adaptation: boolean
  /** Code de EnrollmentAdaptationType. Une ligne ancienne diffère parfois. */
  adaptation_type: string | null
  /** Type disability ou mixed : donnée de santé. Null hors lecteurs. */
  adaptation_details: string | null
  /** Détail de l'adaptation renseigné : lisible de tout lecteur. */
  has_adaptation_details: boolean
  location_preference: string | null
  schedule_constraints: string | null
  preferred_modality: string | null
  funding_source: string | null
  interview_scheduled_at: string | null
  interview_completed_at: string | null
  interview_duration_minutes: number | null
  interview_notes: string | null
  interview_method: string | null
  is_validated: boolean
  validated_at: string | null
  created_at: string
  updated_at: string
}

export interface EnrollmentConvention {
  id: string
  tenant_id: string
  enrollment_id: string
  convention_number: string
  convention_type: string
  /** Payeur : company, individual ou opco. */
  client_type: string
  /** Statut : `draft` tant que la convention est modifiable. */
  status: string
  of_name: string
  of_siret: string
  of_declaration_number: string
  client_name: string | null
  client_siret: string | null
  training_modality: string
  training_program: string
  evaluation_method: string
  payment_terms: string
  /** Mentions du contrat d'un particulier (art. L6353-4). */
  prerequisites: O.Option<string>
  targeted_qualifications: O.Option<string>
  teaching_resources: O.Option<string>
  distance_learning_terms: O.Option<string>
  trainer_credentials: O.Option<string>
  early_termination_terms: O.Option<string>
  /** Signature de l'organisme, puis du client. */
  of_signed_at: O.Option<string>
  client_signed_at: O.Option<string>
  cgv_accepted: boolean
  internal_rules_accepted: boolean
  gdpr_consent: boolean
  created_at: string
  updated_at: string
}

/** Champs que l'API sert à null quand ils sont absents. */
type ConventionOptionalText =
  | "prerequisites"
  | "targeted_qualifications"
  | "teaching_resources"
  | "distance_learning_terms"
  | "trainer_credentials"
  | "early_termination_terms"
  | "of_signed_at"
  | "client_signed_at"

/** Convention telle que l'API l'envoie : textes facultatifs non décodés. */
type ConventionPayload = Omit<EnrollmentConvention, ConventionOptionalText> & Record<ConventionOptionalText, unknown>

/**
 * Texte servi, absent quand l'API envoie null.
 *
 * @example
 * servedText(null) // O.none
 */
export function servedText(value: unknown): O.Option<string> {
  return typeof value === "string" ? O.some(value) : O.none
}

/** Convention de l'API, textes facultatifs en `Option`. */
function decodeConvention(payload: ConventionPayload): EnrollmentConvention {
  return {
    ...payload,
    prerequisites: servedText(payload.prerequisites),
    targeted_qualifications: servedText(payload.targeted_qualifications),
    teaching_resources: servedText(payload.teaching_resources),
    distance_learning_terms: servedText(payload.distance_learning_terms),
    trainer_credentials: servedText(payload.trainer_credentials),
    early_termination_terms: servedText(payload.early_termination_terms),
    of_signed_at: servedText(payload.of_signed_at),
    client_signed_at: servedText(payload.client_signed_at),
  }
}

export interface EnrollmentSummons {
  id: string
  enrollment_id: string
  summons_type: string
  scheduled_send_at: string | null
  subject: string
  body: string
  sent_at: string | null
  sent_via: string | null
  acknowledged_at: string | null
  created_at: string
}

export interface EnrollmentDocument {
  id: string
  enrollment_id: string
  document_type: string
  workflow_step: number
  file_url: string | null
  file_name: string | null
  generated_at: string
  sent_at: string | null
  received_at: string | null
  signed_at: string | null
  version: number | null
  is_current: boolean
  created_at: string
}

export interface EnrollmentReminder {
  id: string
  enrollment_id: string
  reminder_type: string
  scheduled_at: string
  sent_at: string | null
  retry_count: number | null
  subject: string
  message: string
  channel: string | null
  recipient_email: string | null
  /** Code du statut (pending, sent, failed, cancelled, acknowledged). */
  status: unknown
  created_at: string
  /** Libellés servis par l'API à côté des codes (migration 735). */
  reminder_type_label: string
  /** Libellé du canal, `null` sans canal : à lire avec `apiText`. */
  channel_label: unknown
  /** Libellé du statut, `null` sans statut : à lire avec `apiText`. */
  status_label: unknown
  /** Motif d'un rappel en échec, sinon `null`. Lire avec `apiText`. */
  failure_reason: unknown
}

interface ListEnrollmentsParams {
  page?: number
  per_page?: number
  session_id?: string
  status_code?: string
  workflow_phase?: string
  is_waitlisted?: boolean
  include_cancelled?: boolean
  search?: string
}

export function listEnrollments(params: ListEnrollmentsParams = {}): Promise<PaginatedResponse<EnrollmentWorkflowSummary>> {
  return api.get<PaginatedResponse<EnrollmentWorkflowSummary>>("/enrollments", params as Record<string, unknown>)
}

export function getEnrollment(id: string): Promise<EnrollmentWorkflowDetail> {
  return api.get<EnrollmentWorkflowDetail>(`/enrollments/${id}`)
}

export function getEnrollmentTransitions(id: string): Promise<EnrollmentWorkflowTransition[]> {
  return api.get<EnrollmentWorkflowTransition[]>(`/enrollments/${id}/transitions`)
}

export function transitionEnrollment(id: string, data: { to_status_code: string; reason?: string }): Promise<EnrollmentWorkflowDetail> {
  return api.post<EnrollmentWorkflowDetail>(`/enrollments/${id}/transition`, data)
}

export function getEnrollmentHistory(id: string): Promise<EnrollmentHistoryEntry[]> {
  return api.get<EnrollmentHistoryEntry[]>(`/enrollments/${id}/history`)
}

export function getEnrollmentProgress(id: string): Promise<EnrollmentWorkflowProgress> {
  return api.get<EnrollmentWorkflowProgress>(`/enrollments/${id}/progress`)
}

export function getEnrollmentNeedsAnalysis(id: string): Promise<EnrollmentNeedsAnalysis> {
  return api.get<EnrollmentNeedsAnalysis>(`/enrollments/${id}/needs-analysis`)
}

export function getEnrollmentConvention(id: string): Promise<EnrollmentConvention> {
  return api.get<ConventionPayload>(`/enrollments/${id}/convention`).then(decodeConvention)
}

export function createEnrollmentConvention(id: string, data: Record<string, unknown>): Promise<EnrollmentConvention> {
  return api.post<ConventionPayload>(`/enrollments/${id}/convention`, data).then(decodeConvention)
}

/**
 * Écrit les champs envoyés sur la convention en brouillon.
 * Les champs absents restent inchangés.
 *
 * @example
 * updateEnrollmentConvention(enrollmentId, { prerequisites: "Aucun prérequis." }) // convention relue
 */
// comply-ignore: api-branded-id-types — les services du dépôt prennent des identifiants chaîne.
export function updateEnrollmentConvention(id: string, mentions: Record<string, string>): Promise<EnrollmentConvention> {
  return api.put<ConventionPayload>(`/enrollments/${id}/convention`, mentions).then(decodeConvention)
}

export function sendConventionForSignature(id: string): Promise<{ success: boolean; message: string }> {
  return api.post(`/enrollments/${id}/convention/send`, {})
}

export function listEnrollmentSummons(id: string): Promise<EnrollmentSummons[]> {
  return api.get<EnrollmentSummons[]>(`/enrollments/${id}/summons`)
}

export function createEnrollmentSummons(id: string, data: { subject: string; body: string; summons_type?: string }): Promise<EnrollmentSummons> {
  return api.post<EnrollmentSummons>(`/enrollments/${id}/summons`, data)
}

export function sendSummons(summonsId: string): Promise<{ success: boolean; message: string }> {
  return api.post(`/summons/${summonsId}/send`, {})
}

export function listEnrollmentDocuments(id: string, params?: { document_type?: string; workflow_step?: number }): Promise<EnrollmentDocument[]> {
  return api.get<EnrollmentDocument[]>(`/enrollments/${id}/documents`, params as Record<string, unknown>)
}

export function createEnrollmentDocument(id: string, data: { document_type: string; workflow_step: number; file_url?: string; file_name?: string }): Promise<EnrollmentDocument> {
  return api.post<EnrollmentDocument>(`/enrollments/${id}/documents`, data)
}

export function listEnrollmentReminders(id: string): Promise<EnrollmentReminder[]> {
  return api.get<EnrollmentReminder[]>(`/enrollments/${id}/reminders`)
}

export function createEnrollmentReminder(id: string, data: { reminder_type: string; scheduled_at: string; subject: string; message: string; channel?: string; recipient_email?: string }): Promise<EnrollmentReminder> {
  return api.post<EnrollmentReminder>(`/enrollments/${id}/reminders`, data)
}

export function cancelEnrollmentReminder(reminderId: string): Promise<void> {
  return api.post(`/enrollment-reminders/${reminderId}/cancel`, {})
}

export function cancelEnrollment(id: string, data: { reason?: string }): Promise<EnrollmentWorkflowDetail> {
  return api.post<EnrollmentWorkflowDetail>(`/enrollments/${id}/cancel`, data)
}
